import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Badge, Btn, Card, MetricCard, Avatar, ProgressBar, DataTable, FormRow, Field, Modal, SearchBar, Pagination, FormActions, Tabs, Alert } from '../../components/ui';
import { ConfirmModal } from '../../components/shared';
import * as maintenanceService from '../../api/maintenanceService';
import { getBillingConfig, getBillingComponents } from '../../api/billing';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';
import { toastError, toastSuccess } from '../../utils/toast';
import { getFieldError } from '../../utils/apiUtils';
import { Icon } from '../../icons';
import { IonItemSliding, IonItem, IonItemOptions, IonItemOption } from '@ionic/react';

const translations = { en, hi, gu };

// Compute 12 months for an Indian Financial Year (Apr–Mar)
const getMonthsForFY = (fy) => {
  if (!fy || !fy.includes('-')) return [];
  const sy = parseInt(fy.split('-')[0]);
  const months = [];
  for (let m = 4; m <= 15; m++) {
    const mo = m > 12 ? m - 12 : m;
    const yr = m > 12 ? sy + 1 : sy;
    const d = new Date(yr, mo - 1, 1);
    months.push(d.toLocaleString('en-US', { month: 'short' }) + ' ' + yr);
  }
  return months;
};

const getAvailableFYs = () => {
  const now = new Date();
  const cur = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return [cur - 2, cur - 1, cur].map(y => `${y}-${String(y + 1).slice(-2)}`);
};

const normalizeBillComponents = (unit) => {
  const components = (unit?.bill_components || []).map(c => ({
    head: c.component_name,
    amount: c.amount,
  }));

  if (!components.length) return components;

  const hasMaintenance = components.some(c => String(c.head || '').trim().toLowerCase() === 'maintenance charges');
  if (hasMaintenance) return components;

  const total = parseFloat(unit?.monthly_bill_amount || 0);
  const componentTotal = components.reduce((sum, component) => sum + (parseFloat(component.amount) || 0), 0);
  const base = Math.max(0, total - componentTotal);

  if (base > 0) {
    return [{ head: 'Maintenance Charges', amount: base }, ...components];
  }

  return components;
};

const DEF_UNIT_FORM = { block: "", unit: "", floor: "", unit_type: "2BHK", ownership_type: "Owner", owner: "", phone: "", email: "", move_in_date: new Date().toISOString().slice(0, 10), tenant_name: "", tenant_phone: "", vehicles: [""], monthly_bill: 0, bill_components: [], arrears: 0, arrears_description: "", advance_balance: 0 };

function MonthBadge({ status, title, disabled, t }) {
  if (disabled) return <span title={t("label_not_applicable")} className="text-slate-300 font-bold text-sm cursor-default">-</span>;
  if (status === "Upcoming") return <span title={t("label_upcoming_not_billed")} className="text-slate-300 font-bold text-sm cursor-default">-</span>;
  if (status === "Paid") return <span title={title} className="text-emerald-600 dark:text-emerald-500 font-bold text-sm cursor-default">✓</span>;
  if (status === "Partial") return <span title={title} className="text-amber-500 font-bold text-sm cursor-default">~</span>;
  return <span title={title} className="text-red-500 dark:text-red-400 font-bold text-sm cursor-default">✗</span>;
}

const ReceiptCard = ({ unit, entry, t }) => {
  return (
    <div style={{ border: "1px solid var(--color-border)", borderRadius: 12, overflow: "hidden", fontSize: 12 }}>
      <div style={{ background: "linear-gradient(135deg,#4f46e5,#7c3aed)", padding: "14px 20px", color: "#fff" }}>
        <div style={{ fontWeight: 900, fontSize: 15, letterSpacing: "-0.01em" }}>{t("label_the_society")}</div>
        <div style={{ opacity: 0.7, fontSize: 10 }}>The Clover App</div>
      </div>
      <div style={{ padding: "16px 20px", background: "var(--color-surface)" }}>
        <div style={{ textAlign: "center", fontSize: 13, fontWeight: 800, letterSpacing: "0.12em", marginBottom: 10, color: "var(--color-ink)" }}>{t("modal_payment_receipt")}</div>
        <div style={{ textAlign: "center", fontSize: 11, color: "var(--color-muted)", marginBottom: 10 }}>Receipt No: <strong style={{ color: "var(--color-ink)" }}>{entry?.receipt_no || "-"}</strong></div>
        <hr style={{ border: "none", borderTop: "1px dashed var(--color-border)", margin: "8px 0" }} />
        <div style={{ display: "grid", gridTemplateColumns: "110px 1fr", rowGap: 3, fontSize: 11, color: "var(--color-muted)" }}>
          {[[t("label_unit"), unit.unit], ["Floor", unit.floor], [t("label_owner"), <strong style={{ color: "var(--color-ink)" }}>{unit.owner}</strong>], ["Phone", unit.phone]].map(([l, v]) => (
            <><span key={l + "l"}>{l}:</span><span key={l + "v"}>{v}</span></>
          ))}
        </div>
        <hr style={{ border: "none", borderTop: "1px dashed var(--color-border)", margin: "8px 0" }} />
        <div style={{ fontSize: 11, color: "var(--color-muted)" }}>
          <div style={{ marginBottom: 4 }}><strong>For Month:</strong> {entry?.month}</div>
          <div style={{ marginBottom: 4, fontWeight: 600 }}>Bill Components:</div>
          {unit.bill_components.map((c, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", paddingLeft: 10, marginBottom: 2 }}>
              <span>{c.head}</span><span>₹{Number(c.amount).toLocaleString()}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid var(--color-border)", marginTop: 6, paddingTop: 4, fontWeight: 700 }}>
            <span>Total Billed:</span><span>₹{unit.monthly_bill.toLocaleString()}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
            <span>Amount Paid:</span><span style={{ color: "#059669" }}>₹{entry?.amount?.toLocaleString()}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
            <span>Balance Due:</span>
            <span style={{ color: entry?.amount >= unit.monthly_bill ? "var(--color-muted)" : "#ef4444" }}>
              {entry?.amount >= unit.monthly_bill ? t("label_nil") : `₹${(unit.monthly_bill - (entry?.amount || 0)).toLocaleString()}`}
            </span>
          </div>
        </div>
        <hr style={{ border: "none", borderTop: "1px dashed var(--color-border)", margin: "8px 0" }} />
        <div style={{ display: "grid", gridTemplateColumns: "110px 1fr", rowGap: 3, fontSize: 11, color: "var(--color-muted)" }}>
          {[["Mode", entry?.mode], ["Txn Ref", entry?.ref || "-"], ["Date", entry?.date], ["Received By", entry?.received_by]].map(([l, v]) => (
            <><span key={l + "l"}>{l}:</span><span key={l + "v"}>{v}</span></>
          ))}
        </div>
        <hr style={{ border: "none", borderTop: "1px dashed var(--color-border)", margin: "8px 0" }} />
        <div style={{ textAlign: "center", fontSize: 10, color: "var(--color-muted)" }}>{t("msg_computer_generated")}</div>
      </div>
    </div>
  );
}

const UnitFormBody = ({ form, setForm, ff, errors, t, isEdit, existingUnitTypes = [], onAddNewUnitType, onDeleteUnitType }) => {
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const unitTypes = [...new Set([...existingUnitTypes, "1RK", "1BHK", "2BHK", "3BHK", "Penthouse", "Shop"])].concat(["+ Add New", "Manage Types..."]);
  return (
    <>
      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 mt-1">{t("form_unit_details")}</div>
      <FormRow cols={2}>
        <Field label="Block" value={form.block} onChange={ff("block")} placeholder="e.g. A" required error={getFieldError(errors, "block")} />
        <Field label={t("form_unit_no")} value={form.unit} onChange={(e) => {
          const val = e.target.value.replace(/\D/g, '');
          setForm(f => {
            let newFloor = f.floor;
            if (!isEdit) {
              const oldMatch = (f.unit || '').match(/\d/);
              const oldDigit = oldMatch ? oldMatch[0] : "";
              const newMatch = val.match(/\d/);
              const newDigit = newMatch ? newMatch[0] : "";

              if (!f.floor || f.floor === oldDigit) {
                newFloor = newDigit;
              }
            }
            return { ...f, unit: val, floor: newFloor };
          });
          ff("unit")({ target: { value: val } });
        }} required placeholder="e.g. 101" error={getFieldError(errors, "unit_number")} />
      </FormRow>
      <FormRow cols={2}>
        <Field label={t("form_floor")} value={form.floor} onChange={ff("floor")} placeholder="e.g. 1st Floor" />
        <Field label={t("form_unit_type")} type="select" value={form.unit_type} onChange={(e) => {
          if (e.target.value === "+ Add New") {
            const newType = window.prompt("Enter new unit type (e.g. 4 BHK):");
            if (newType && newType.trim() !== "") {
              if (onAddNewUnitType) onAddNewUnitType(newType.trim());
              setForm(f => ({ ...f, unit_type: newType.trim() }));
            } else {
              setForm(f => ({ ...f, unit_type: f.unit_type || "1BHK" }));
            }
          } else if (e.target.value === "Manage Types...") {
            setManageModalOpen(true);
            setForm(f => ({ ...f, unit_type: f.unit_type || "1BHK" }));
          } else {
            ff("unit_type")(e);
          }
        }} options={unitTypes} />
      </FormRow>
      <FormRow cols={1}>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">{t("form_ownership")}</label>
          <div className="flex gap-2">
            {[t("label_owner"), "Tenant"].map(t => <button key={t} type="button" onClick={() => setForm(f => ({ ...f, ownership_type: t }))} className={`px-4 py-2 rounded-lg text-xs font-semibold border cursor-pointer transition-all ${form.ownership_type === t ? "bg-primary text-white border-primary" : "bg-white dark:bg-slate-800 text-muted border-border hover:border-primary/40"}`}>{t}</button>)}
          </div>
        </div>
      </FormRow>
      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 mt-3">{t("form_owner_info")}</div>
      <FormRow cols={2}>
        <Field 
          label={t("form_owner_name")} 
          value={form.owner} 
          onChange={(e) => {
            const val = e.target.value.replace(/[0-9]/g, '');
            ff("owner")({ target: { value: val } });
          }} 
          placeholder="e.g. John Doe" 
          required 
          error={getFieldError(errors, "owner_name")} 
        />
        <Field label={t("form_phone")} value={form.phone} onChange={ff("phone")} placeholder="e.g. 9876543210" required error={getFieldError(errors, "phone")} />
      </FormRow>
      <FormRow cols={2}><Field label={t("form_email")} type="email" value={form.email} onChange={ff("email")} placeholder="e.g. user@example.com" required error={getFieldError(errors, "email")} /><Field label={t("form_move_in")} type="date" value={form.move_in_date} onChange={ff("move_in_date")} required error={getFieldError(errors, "move_in_date")} /></FormRow>
      {form.ownership_type === "Tenant" && <>
        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 mt-3">{t("form_tenant_info")}</div>
        <FormRow cols={2}>
          <Field 
            label={t("form_tenant_name")} 
            value={form.tenant_name} 
            onChange={(e) => {
              const val = e.target.value.replace(/[0-9]/g, '');
              ff("tenant_name")({ target: { value: val } });
            }} 
            placeholder="e.g. Jane Doe" 
            error={getFieldError(errors, "tenant_name")}
          />
          <Field label={t("form_tenant_phone")} value={form.tenant_phone} onChange={ff("tenant_phone")} placeholder="e.g. 9876543210" error={getFieldError(errors, "tenant_phone")} />
        </FormRow>
        <p className="text-xs text-muted -mt-2 mb-3">{t("msg_owner_details_billing")}</p>
      </>}
      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 mt-3">{t("form_vehicles")}</div>
      {form.vehicles.map((v, i) => (
        <div key={i} className="flex gap-2 mb-2">
          <input
            type="text"
            placeholder={t("placeholder_vehicle_no") || "MH02AB1234"}
            value={v}
            maxLength={12}
            autoCapitalize="characters"
            autoCorrect="off"
            autoComplete="off"
            spellCheck={false}
            aria-autocomplete="none"
            data-gramm="false"
            style={{ textTransform: "uppercase", textDecoration: "none" }}
            onChange={e => {
              const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
              setForm(f => ({ ...f, vehicles: f.vehicles.map((x, j) => j === i ? val : x) }));
            }}
            onPaste={e => {
              e.preventDefault();
              const pasteText = (e.clipboardData || window.clipboardData).getData('text');
              const cleaned = pasteText.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 12);
              setForm(f => ({ ...f, vehicles: f.vehicles.map((x, j) => j === i ? cleaned : x) }));
            }}
            onBlur={e => {
              const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
              setForm(f => ({ ...f, vehicles: f.vehicles.map((x, j) => j === i ? val : x) }));
            }}
            className="flex-1 px-3 py-2 border border-border rounded-lg text-sm bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 uppercase font-mono tracking-wider no-underline"
          />
          {form.vehicles.length > 1 && <button type="button" onClick={() => setForm(f => ({ ...f, vehicles: f.vehicles.filter((_, j) => j !== i) }))} className="px-2.5 border border-red-200 dark:border-red-400 text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/20 cursor-pointer bg-transparent text-sm">×</button>}
        </div>
      ))}
      {form.vehicles.length < 3 && <button type="button" onClick={() => setForm(f => ({ ...f, vehicles: [...f.vehicles, ""] }))} className="text-xs text-primary font-semibold cursor-pointer bg-transparent border-0 mb-3">{t("form_add_vehicle")}</button>}
      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 mt-1">{t("form_maint_setup")}</div>
      <FormRow><Field label={t("form_monthly_bill")} type="number" value={form.monthly_bill} readOnly disabled className="bg-slate-100 dark:bg-slate-800/50 cursor-not-allowed font-semibold text-slate-700 dark:text-slate-300" /></FormRow>
      <div className="mb-3">
        <label className="block text-xs font-semibold text-slate-500 mb-1.5">{t("form_bill_components")}</label>
        {form.bill_components.map((c, i) => (
          <div key={i} className="flex gap-2 mb-1.5">
            <input placeholder={t("placeholder_head_name")} value={c.head} onChange={e => setForm(f => ({ ...f, bill_components: f.bill_components.map((x, j) => j === i ? { ...x, head: e.target.value } : x) }))} className="flex-1 px-3 py-2 border border-border rounded-lg text-sm bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20" />
            <input type="number" placeholder="₹" value={c.amount} onChange={e => setForm(f => {
              const updated = f.bill_components.map((x, j) => j === i ? { ...x, amount: e.target.value } : x);
              const total = updated.reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);
              return { ...f, bill_components: updated, monthly_bill: total };
            })} className="w-24 px-3 py-2 border border-border rounded-lg text-sm bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20" />
            {form.bill_components.length > 1 && <button onClick={() => setForm(f => {
              const updated = f.bill_components.filter((_, j) => j !== i);
              const total = updated.reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);
              return { ...f, bill_components: updated, monthly_bill: total };
            })} className="px-2.5 border border-red-200 dark:border-red-400 text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/20 cursor-pointer bg-transparent text-sm">×</button>}
          </div>
        ))}
        <button onClick={() => setForm(f => ({ ...f, bill_components: [...f.bill_components, { head: "", amount: "" }] }))} className="text-xs text-primary font-semibold cursor-pointer bg-transparent border-0">{t("form_add_component")}</button>
      </div>
      <FormRow cols={2}><Field label={t("form_opening_arrears")} type="number" value={form.arrears} onChange={ff("arrears")} placeholder="0" note={t("note_carry_forward")} /><Field label="Arrears Description" value={form.arrears_description} onChange={ff("arrears_description")} placeholder="e.g. Balance from old system" /></FormRow>
      <FormRow cols={1}><Field label={t("form_advance_balance")} type="number" value={form.advance_balance} onChange={ff("advance_balance")} placeholder="0" /></FormRow>

      {manageModalOpen && (
        <Modal title="Manage Unit Types" onClose={() => setManageModalOpen(false)}>
          <div className="space-y-2">
            {existingUnitTypes.map(type => (
              <div key={type} className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-border">
                <span className="font-medium text-sm">{type}</span>
                <button type="button" onClick={() => onDeleteUnitType && onDeleteUnitType(type)} className="text-red-500 hover:text-red-700 w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors">
                  {/* <i className="fa-solid fa-trash text-sm"></i> */}
                  <Icon name="trash" size={20} color="white" />
                </button>
              </div>
            ))}
            {existingUnitTypes.length === 0 && <div className="text-sm text-muted py-4 text-center">No custom unit types found.</div>}
            <div className="text-xs text-muted mt-4">
              Note: Types currently used by any unit cannot be permanently hidden.
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default function MaintenanceScreen() {
  const { language } = useSettings();
  const t = (key) => translations[language]?.maintenance?.[key] || translations.en.maintenance?.[key] || key;
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const canCreate = hasPermission('maintenance.create');
  const canUpdate = hasPermission('maintenance.update');
  const canDelete = hasPermission('maintenance.delete');
  const canPay = hasPermission('maintenance.payments');
  const canExport = hasPermission('maintenance.export');
  const canRemind = hasPermission('maintenance.reminders');

  const isStrictMember = (user?.primary_role_label === 'Member' || user?.roles_list?.includes('member')) &&
    !user?.roles_list?.includes('super_admin') &&
    !user?.roles_list?.includes('admin');

  const [existingUnitTypes, setExistingUnitTypes] = useState([]);
  const handleAddNewUnitType = useCallback((newType) => {
    setExistingUnitTypes(prev => [...new Set([...prev, newType])]);
    maintenanceService.storeUnitType(newType).catch(() => { });
  }, []);
  const handleDeleteUnitType = useCallback((typeToRemove) => {
    setExistingUnitTypes(prev => prev.filter(t => t !== typeToRemove));
    maintenanceService.deleteUnitType(typeToRemove).catch(() => { });
  }, []);
  const [units, setUnits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [mainTab, setMainTab] = useState(0);
  const [statusFilter, setStatusFilter] = useState("All");
  const [blockFilter, setBlockFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [unitLimit, setUnitLimit] = useState(-1);


  const [addUnitModal, setAddUnitModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [addUnitForm, setAddUnitForm] = useState(DEF_UNIT_FORM);
  const [billingDefaultsLoading, setBillingDefaultsLoading] = useState(false);
  const [savingAddUnit, setSavingAddUnit] = useState(false);

  const [unitDetailModal, setUnitDetailModal] = useState(null);
  const [unitDetailTab, setUnitDetailTab] = useState(0);
  const [detailFY, setDetailFY] = useState("All");
  const [selectedMonthEntry, setSelectedMonthEntry] = useState(null);
  const [loadingReceiptId, setLoadingReceiptId] = useState(null);
  const [loadingStatement, setLoadingStatement] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingGenerateBills, setLoadingGenerateBills] = useState(false);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [editUnitForm, setEditUnitForm] = useState(null);
  const [savingEditUnit, setSavingEditUnit] = useState(false);
  const [remindingUnitId, setRemindingUnitId] = useState(null);

  const defaultFY = (() => { const n = new Date(); return (n.getMonth() >= 3 ? n.getFullYear() : n.getFullYear() - 1) + '-' + String((n.getMonth() >= 3 ? n.getFullYear() + 1 : n.getFullYear())).slice(-2); })();
  const [yearlyViewFY, setYearlyViewFY] = useState(defaultFY);
  const [monthlyViewFY, setMonthlyViewFY] = useState(defaultFY);
  const [yearlyData, setYearlyData] = useState(null);
  const [yearlyLoading, setYearlyLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [receiptModal, setReceiptModal] = useState(null);
  const [exportPopover, setExportPopover] = useState(false);
  const [deleteUnitId, setDeleteUnitId] = useState(null);
  const [apiErrors, setApiErrors] = useState(null);
  const [editApiErrors, setEditApiErrors] = useState(null);
  const exportRef = useRef(null);

  // --- Invoice System State ---
  const [settleModal, setSettleModal] = useState(null); // unit object
  const [settleInvoices, setSettleInvoices] = useState([]); // invoices for settle modal
  const [settleOverrides, setSettleOverrides] = useState({}); // { [invoice_id]: amount }
  const [settleForm, setSettleForm] = useState({ received_amount: '', payment_mode: 'UPI', reference_number: '', payment_date: new Date().toISOString().slice(0, 10) });
  const [settleLoading, setSettleLoading] = useState(false);
  const [ledgerModal, setLedgerModal] = useState(null); // unit object
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerTab, setLedgerTab] = useState("all");
  const [expandedInvoices, setExpandedInvoices] = useState({});
  const [billingConfig, setBillingConfig] = useState(null);

  const [customInvoiceModal, setCustomInvoiceModal] = useState(null);
  const [customInvoiceForm, setCustomInvoiceForm] = useState({ description: '', amount: '', due_date: new Date().toISOString().slice(0, 10) });
  const [customInvoiceLoading, setCustomInvoiceLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const statsRes = await maintenanceService.getDashboardStats();
      setStats(statsRes.data);

      getBillingConfig().then(res => {
        setBillingConfig(res?.data?.data || res?.data || {});
      }).catch(() => { });

      maintenanceService.getUnitTypes().then(res => {
        setExistingUnitTypes(res?.data?.data || res?.data || []);
      }).catch(() => { });

      const unitsRes = await maintenanceService.getUnits(page, statusFilter, search, monthlyViewFY, blockFilter);
      // Map API units to the UI expected format
      const mappedUnits = unitsRes.data.data.map(u => ({
        ...u,
        id: u.id,
        block: u.block || '-',
        unit: u.unit_number,
        floor: u.floor || '-',
        unit_type: u.unit_type || '-',
        ownership_type: u.ownership_type,
        owner: u.owner_name,
        phone: u.phone,
        email: u.email || '',
        move_in_date: u.move_in_date || '',
        tenant_name: u.tenant_name || '',
        tenant_phone: u.tenant_phone || '',
        monthly_bill: parseFloat(u.monthly_bill_amount || 0),
        arrears: parseFloat(u.opening_arrears || 0),
        advance_balance: parseFloat(u.advance_balance || 0),
        vehicles: u.vehicles ? u.vehicles.map(v => v.vehicle_number) : [],
        bill_components: normalizeBillComponents(u),
        paid: parseFloat(u.paid_amount || 0),
        status: u.computed_status || 'Unpaid',
        history: [], // Will load on unit detail
        opening_arrears_fy: parseFloat(u.opening_arrears_fy || 0),
        opening_advance_fy: parseFloat(u.opening_advance_fy || 0),
      }));
      setUnits(mappedUnits);
      setTotalPages(unitsRes.data.last_page);
      setTotalRecords(unitsRes.data.total || mappedUnits.length);
      setUnitLimit(unitsRes.data.unit_limit ?? -1);
    } catch (err) {
      toastError("Error loading data");
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search, monthlyViewFY, blockFilter]);

  const fetchYearlySummary = useCallback(async (fy) => {
    setYearlyLoading(true);
    try {
      const res = await maintenanceService.getYearlySummary(fy);
      setYearlyData(res.data);
    } catch (err) {
      toastError('Failed to load yearly summary');
    } finally {
      setYearlyLoading(false);
    }
  }, []);

  const refreshData = useCallback(() => {
    fetchData();
    if (mainTab === 1) {
      fetchYearlySummary(yearlyViewFY);
    }
  }, [fetchData, fetchYearlySummary, yearlyViewFY, mainTab]);

  const lastFetchParams = useRef(null);

  useEffect(() => {
    const currentParams = JSON.stringify({ page, statusFilter, search, mainTab, yearlyViewFY, monthlyViewFY });
    if (lastFetchParams.current === currentParams) return;
    lastFetchParams.current = currentParams;
    refreshData();
  }, [refreshData, page, statusFilter, search, mainTab, monthlyViewFY, yearlyViewFY]);

  useEffect(() => {
    if (!exportPopover) return;
    const h = e => { if (exportRef.current && !exportRef.current.contains(e.target)) setExportPopover(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [exportPopover]);

  useEffect(() => {
    if (unitDetailTab === 3 && unitDetailModal) {
      let defaultBlock = unitDetailModal.block && unitDetailModal.block !== '-' ? unitDetailModal.block : "";
      if (!defaultBlock && unitDetailModal.unit) {
        const blockMatch = unitDetailModal.unit.match(/^([a-zA-Z]+)/);
        if (blockMatch) defaultBlock = blockMatch[1];
      }
      setEditUnitForm({ block: defaultBlock, unit: unitDetailModal.unit, floor: unitDetailModal.floor, unit_type: unitDetailModal.unit_type, ownership_type: unitDetailModal.ownership_type, owner: unitDetailModal.owner, phone: unitDetailModal.phone, email: unitDetailModal.email, move_in_date: unitDetailModal.move_in_date || "", tenant_name: unitDetailModal.tenant_name || "", tenant_phone: unitDetailModal.tenant_phone || "", vehicles: unitDetailModal.vehicles?.length ? [...unitDetailModal.vehicles] : [""], monthly_bill: unitDetailModal.monthly_bill, bill_components: unitDetailModal.bill_components.map(c => ({ ...c })), arrears: unitDetailModal.arrears, arrears_description: unitDetailModal.arrears_description || "", advance_balance: unitDetailModal.advance_balance });
    }
  }, [unitDetailTab, unitDetailModal]);

  useEffect(() => {
    if (!addUnitModal) return undefined;

    let active = true;
    (async () => {
      setBillingDefaultsLoading(true);
      try {
        const [configRes, componentsRes] = await Promise.all([getBillingConfig(), getBillingComponents()]);
        if (!active) return;
        const config = configRes?.data?.data || configRes?.data || {};
        const activeComponents = componentsRes?.data?.data || componentsRes?.data || [];
        const baseAmount = parseFloat(config.monthly_maintenance_amount || 0);
        const extraComponents = activeComponents
          .filter(c => c.is_active !== false)
          .map(c => ({ head: c.name, amount: c.amount }));
        const billComponents = [
          { head: 'Maintenance Charges', amount: baseAmount },
          ...extraComponents,
        ];
        setAddUnitForm(prev => ({
          ...prev,
          monthly_bill: billComponents.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0),
          bill_components: billComponents,
        }));
      } catch {
        if (active) toastError('Failed to load billing defaults.');
      } finally {
        if (active) setBillingDefaultsLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [addUnitModal]);

  const openUnitDetail = async (unit, tab = 0, fy = defaultFY) => {
    try {
      const res = await maintenanceService.getUnitDetails(unit.id);
      const u = res.data;
      let computedArrears = 0;
      let computedPenalties = 0;
      if (u.invoices && Array.isArray(u.invoices)) {
        u.invoices.forEach(inv => {
          if (['pending', 'partial', 'overdue'].includes(inv.status)) {
            computedArrears += parseFloat(inv.balance_amount || 0);
            computedPenalties += parseFloat(inv.penalty_amount || 0);
          }
        });
      } else {
        computedArrears = parseFloat(u.opening_arrears || 0);
      }

      // Also add the base opening arrears to computed arrears if invoices existed
      if (u.invoices && Array.isArray(u.invoices)) {
        computedArrears += parseFloat(u.opening_arrears || 0);
      }

      const fresh = {
        id: u.id,
        block: u.block || '',
        unit: u.unit_number,
        floor: u.floor || '-',
        unit_type: u.unit_type || '-',
        ownership_type: u.ownership_type,
        owner: u.owner_name,
        phone: u.phone,
        email: u.email || '',
        move_in_date: u.move_in_date || '',
        tenant_name: u.tenant_name || '',
        tenant_phone: u.tenant_phone || '',
        monthly_bill: parseFloat(u.monthly_bill_amount || 0),
        arrears: parseFloat(u.opening_arrears || 0),
        penalties_due: computedPenalties,
        advance_balance: parseFloat(u.advance_balance || 0),
        vehicles: u.vehicles ? u.vehicles.map(v => v.vehicle_number) : [],
        bill_components: normalizeBillComponents(u),
        paid: parseFloat(u.paid || 0),
        status: u.status || 'Unpaid',
        history: u.history || [],
        invoices: u.invoices || [],
        arrears_description: u.opening_arrear_description || '',
      };
      setUnitDetailModal(fresh);
      setUnitDetailTab(tab);
      setDetailFY(fy);
      setSelectedMonthEntry(null);
    } catch (err) {
      toastError("Error loading unit details");
    }
  };


  const isValidPhone = (p) => /^[+]?[0-9]{10,15}$/.test(p.replace(/[\s-]/g, ''));
  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const scrollToFirstError = () => {
    setTimeout(() => {
      const firstErrorEl = document.querySelector('[data-invalid="true"], [aria-invalid="true"], .\\!border-red-500, .border-red-500');
      if (firstErrorEl) {
        firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstErrorEl.focus?.();
      }
    }, 100);
  };

  const saveAddUnit = async () => {
    const errors = {};
    if (!(addUnitForm.block || '').trim()) errors.block = ["Block is required."];
    if (!(addUnitForm.unit || '').trim()) {
      errors.unit_number = ["Unit Number is required."];
    } else if (!/^\d+$/.test(addUnitForm.unit.trim())) {
      errors.unit_number = ["Unit Number must contain only numeric values."];
    }

    if (!addUnitForm.owner.trim()) {
      errors.owner_name = ["Owner Name is required."];
    } else if (/\d/.test(addUnitForm.owner)) {
      errors.owner_name = ["Owner Name cannot contain numbers."];
    }
    
    if (!addUnitForm.phone.trim()) {
      errors.phone = ["Phone number is required."];
    } else if (!isValidPhone(addUnitForm.phone)) {
      errors.phone = ["Please enter a valid phone number."];
    }

    if (!addUnitForm.email.trim()) {
      errors.email = ["Email is required."];
    } else if (!isValidEmail(addUnitForm.email.trim())) {
      errors.email = ["Please enter a valid email address."];
    }

    if (addUnitForm.ownership_type === "Tenant" && addUnitForm.tenant_phone?.trim()) {
      if (!isValidPhone(addUnitForm.tenant_phone)) {
        errors.tenant_phone = ["Please enter a valid phone number."];
      }
    }

    if (!addUnitForm.move_in_date) errors.move_in_date = ["Move-in date is required."];

    if (Object.keys(errors).length > 0) {
      setApiErrors({ errors });
      toastError("Please fill in all required fields.");
      scrollToFirstError();
      return;
    }

    try {
      setSavingAddUnit(true);
      const monthlyBill = addUnitForm.bill_components.reduce((sum, component) => sum + (parseFloat(component.amount) || 0), 0);
      const payload = {
        block: addUnitForm.block,
        unit_number: addUnitForm.unit,
        floor: addUnitForm.floor,
        unit_type: addUnitForm.unit_type,
        ownership_type: addUnitForm.ownership_type,
        owner_name: addUnitForm.owner,
        phone: addUnitForm.phone,
        email: addUnitForm.email,
        move_in_date: addUnitForm.move_in_date,
        tenant_name: addUnitForm.tenant_name,
        tenant_phone: addUnitForm.tenant_phone,
        monthly_bill_amount: monthlyBill || parseFloat(addUnitForm.monthly_bill) || 0,
        opening_arrears: parseFloat(addUnitForm.arrears) || 0,
        opening_arrear_description: addUnitForm.arrears_description || '',
        advance_balance: parseFloat(addUnitForm.advance_balance) || 0,
        vehicles: addUnitForm.vehicles.map(v => v.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()).filter(Boolean),
        bill_components: addUnitForm.bill_components.filter(c => c.head.trim()),
      };
      await maintenanceService.createUnit(payload);
      toastSuccess("Unit added successfully!");
      setAddUnitModal(false);
      setAddUnitForm(DEF_UNIT_FORM);
      setApiErrors(null);
      refreshData();
    } catch (err) {
      if (err.response?.status === 422) {
        setApiErrors(err.response.data);
        toastError("Please fix the validation errors.");
        scrollToFirstError();
      } else {
        toastError(err.response?.data?.message || "Failed to add unit");
      }
    } finally {
      setSavingAddUnit(false);
    }
  };

  const handleSingleReminder = async (unit) => {
    try {
      setRemindingUnitId(unit.id);
      const res = await maintenanceService.sendReminders([unit.id]);
      const sentCount = res.data?.sent ?? 0;
      if (sentCount > 0) {
        toastSuccess(`${sentCount} reminder(s) sent to ${unit.owner}.`);
      } else {
        toastSuccess(`Reminder request processed for ${unit.owner}.`);
      }
    } catch (err) {
      toastError(err?.message || 'Failed to send reminder.');
    } finally {
      setRemindingUnitId(null);
    }
  };

  const saveEditUnit = async () => {
    if (!editUnitForm || !unitDetailModal) return;

    const errors = {};
    if (!(editUnitForm.block || '').trim()) errors.block = ["Block is required."];
    if (!(editUnitForm.unit || '').trim()) {
      errors.unit_number = ["Unit Number is required."];
    } else if (!/^\d+$/.test(editUnitForm.unit.trim())) {
      errors.unit_number = ["Unit Number must contain only numeric values."];
    }

    if (!editUnitForm.owner.trim()) {
      errors.owner_name = ["Owner Name is required."];
    } else if (/\d/.test(editUnitForm.owner)) {
      errors.owner_name = ["Owner Name cannot contain numbers."];
    }
    
    if (!editUnitForm.phone.trim()) {
      errors.phone = ["Phone number is required."];
    } else if (!isValidPhone(editUnitForm.phone)) {
      errors.phone = ["Please enter a valid phone number."];
    }

    if (!editUnitForm.email.trim()) {
      errors.email = ["Email is required."];
    } else if (!isValidEmail(editUnitForm.email.trim())) {
      errors.email = ["Please enter a valid email address."];
    }

    if (editUnitForm.ownership_type === "Tenant" && editUnitForm.tenant_phone?.trim()) {
      if (!isValidPhone(editUnitForm.tenant_phone)) {
        errors.tenant_phone = ["Please enter a valid phone number."];
      }
    }

    if (!editUnitForm.move_in_date) errors.move_in_date = ["Move-in date is required."];

    if (Object.keys(errors).length > 0) {
      setEditApiErrors({ errors });
      toastError("Please fill in all required fields.");
      scrollToFirstError();
      return;
    }

    try {
      setSavingEditUnit(true);
      const payload = {
        block: editUnitForm.block,
        unit_number: editUnitForm.unit,
        floor: editUnitForm.floor,
        unit_type: editUnitForm.unit_type,
        ownership_type: editUnitForm.ownership_type,
        owner_name: editUnitForm.owner,
        phone: editUnitForm.phone,
        email: editUnitForm.email,
        move_in_date: editUnitForm.move_in_date,
        tenant_name: editUnitForm.tenant_name,
        tenant_phone: editUnitForm.tenant_phone,
        monthly_bill_amount: parseFloat(editUnitForm.monthly_bill) || 0,
        opening_arrears: parseFloat(editUnitForm.arrears) || 0,
        opening_arrear_description: editUnitForm.arrears_description || '',
        advance_balance: parseFloat(editUnitForm.advance_balance) || 0,
        vehicles: editUnitForm.vehicles.map(v => v.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()).filter(Boolean),
        bill_components: editUnitForm.bill_components.filter(c => c.head.trim()),
      };
      await maintenanceService.updateUnit(unitDetailModal.id, payload);
      toastSuccess("Unit updated successfully!");
      setUnitDetailTab(0);
      setEditApiErrors(null);
      refreshData();
      openUnitDetail(unitDetailModal, 0, detailFY); // Refresh detail view
    } catch (err) {
      if (err.response?.status === 422) {
        setEditApiErrors(err.response.data);
        toastError("Please fix the validation errors.");
        scrollToFirstError();
      } else {
        toastError("Failed to update unit");
      }
    } finally {
      setSavingEditUnit(false);
    }
  };

  const deleteUnit = async () => {
    if (!unitDetailModal) return;
    try {
      await maintenanceService.deleteUnit(unitDetailModal.id);
      setUnitDetailModal(null);
      setDeleteUnitId(null);
      toastSuccess("Unit deleted.");
      refreshData();
    } catch (err) {
      toastError("Failed to delete unit");
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await maintenanceService.downloadImportTemplate();
      toastSuccess("Template downloaded.");
    } catch (err) {
      toastError("Failed to download template.");
    }
  };

  const handleImportSubmit = async () => {
    if (!importFile) {
      toastError("Please select a file to import.");
      return;
    }

    setImporting(true);
    setApiErrors(null);
    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const res = await maintenanceService.importUnits(formData);
      toastSuccess(res.data?.message || "Import completed successfully.");
      setImportModal(false);
      setImportFile(null);
      refreshData();
    } catch (err) {
      if (err.response?.status === 206) {
        toastError(err.response.data?.message || "Import completed with errors.");
        setApiErrors({ errors: { general: err.response.data?.data?.errors || [] } });
      } else {
        toastError(err.response?.data?.message || "Failed to import units.");
      }
    } finally {
      setImporting(false);
    }
  };

  const toggleRow = id => setSelectedRows(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleGenerateBills = async () => {
    try {
      setLoadingGenerateBills(true);
      const res = await maintenanceService.generateBills({ block: blockFilter });
      toastSuccess(`${res.generated || res.data?.generated || 0} bills generated successfully.`);
      refreshData();
    } catch (err) {
      toastError("Failed to generate bills.");
    } finally {
      setLoadingGenerateBills(false);
    }
  };
  const toggleAll = () => setSelectedRows(prev => prev.size === units.length ? new Set() : new Set(units.map(u => u.id)));

  const paginated = units;

  const totalCollected = units.reduce((s, u) => s + u.paid, 0);
  const totalBill = units.reduce((s, u) => s + u.monthly_bill, 0);
  const totalArrears = units.reduce((s, u) => s + (u.arrears || 0), 0);

  const fyMonthly = yearlyData?.monthly || [];
  const fyUnitWise = yearlyData?.unit_wise || [];
  const fyMonths = yearlyData?.months || [];
  const fyMonthsShort = fyMonths.map(m => m.split(' ')[0]);
  const currentMonthStr = (() => { const d = new Date(); return d.toLocaleString('en-US', { month: 'short' }) + ' ' + d.getFullYear(); })();
  const fyTotalBilled = fyMonthly.reduce((s, m) => s + m.billed, 0);
  const fyTotalCollected = fyMonthly.reduce((s, m) => s + m.collected, 0);
  const fyTotalPending = fyMonthly.reduce((s, m) => s + m.pending, 0);
  const fyCollectionPct = fyTotalBilled ? Math.round(fyTotalCollected / fyTotalBilled * 100) : 0;

  const auForm = addUnitForm;
  const setAuForm = setAddUnitForm;
  const auf = k => e => {
    const val = e.target.value;
    setAuForm(f => ({ ...f, [k]: val }));
    setApiErrors(prev => {
      if (!prev?.errors) return null;
      const next = { ...prev.errors };
      delete next[k];
      delete next[k === 'unit' ? 'unit_number' : k === 'owner' ? 'owner_name' : k];
      return Object.keys(next).length > 0 ? { ...prev, errors: next } : null;
    });
  };

  const setEuForm = setEditUnitForm;
  const euf = k => e => {
    const val = e.target.value;
    setEuForm(f => f ? { ...f, [k]: val } : f);
    setEditApiErrors(prev => {
      if (!prev?.errors) return null;
      const next = { ...prev.errors };
      delete next[k];
      delete next[k === 'unit' ? 'unit_number' : k === 'owner' ? 'owner_name' : k];
      return Object.keys(next).length > 0 ? { ...prev, errors: next } : null;
    });
  };



  // removed unitFYs variable to avoid mismatched FYs

  // --- Invoice System Functions ---
  const fetchInvoiceDashboard = useCallback(async () => {
    try {
      await maintenanceService.getInvoicesDashboard();
    } catch {
      toastError('Failed to load invoice dashboard.');
    }
  }, []);

  const saveCustomInvoice = async () => {
    if (!customInvoiceModal) return;
    if (!customInvoiceForm.description || !customInvoiceForm.amount || !customInvoiceForm.due_date) {
      toastError("Please fill in all fields.");
      return;
    }
    try {
      setCustomInvoiceLoading(true);
      await maintenanceService.storeCustomInvoice(customInvoiceModal.id, {
        description: customInvoiceForm.description,
        amount: parseFloat(customInvoiceForm.amount),
        due_date: customInvoiceForm.due_date
      });
      toastSuccess("Custom charge added successfully.");
      setCustomInvoiceModal(null);
      setCustomInvoiceForm({ description: '', amount: '', due_date: new Date().toISOString().slice(0, 10) });
      refreshData();
    } catch (err) {
      toastError(err.response?.data?.message || "Failed to add custom charge.");
    } finally {
      setCustomInvoiceLoading(false);
    }
  };

  const openSettleModal = async (unit) => {
    setSettleModal(unit);
    setSettleOverrides({});
    setExpandedInvoices({});
    setSettleForm({ received_amount: '', payment_mode: 'UPI', reference_number: '', payment_date: new Date().toISOString().slice(0, 10) });
    try {
      const res = await maintenanceService.getUnitInvoices(unit.id);
      // res is already the unpacked JSON: { success: true, data: [...] }
      setSettleInvoices(res.data || []);
    } catch {
      toastError('Failed to load invoices.');
      setSettleInvoices([]);
    }
  };

  const handleOverrideChange = (key, value, maxVal) => {
    let val = parseFloat(value);
    if (isNaN(val)) val = '';
    else if (maxVal !== undefined && val > maxVal) val = parseFloat(maxVal);

    const newState = { ...settleOverrides };
    if (Object.keys(newState).length === 0) {
      settlePreview.forEach(inv => {
        if (inv.items && inv.items.length > 0) {
          inv.items.forEach(item => {
            newState[`item_${item.id}`] = item.allocated || 0;
          });
        } else {
          newState[`inv_${inv.id}`] = inv.allocated || 0;
        }
      });
    }
    newState[key] = val;
    setSettleOverrides(newState);
  };

  const handleSettle = async () => {
    if (!settleModal) return;
    const receivedAmt = parseFloat(settleForm.received_amount);
    if (!settleForm.received_amount || isNaN(receivedAmt) || receivedAmt <= 0) {
      toastError('Please enter a valid received amount greater than 0.');
      return;
    }
    if (isOverAllocated) {
      toastError('Total allocated amount exceeds the received amount. Please adjust the allocation.');
      return;
    }
    setSettleLoading(true);
    try {
      const allocations = [];
      settlePreview.forEach(inv => {
        if (inv.items && inv.items.length > 0) {
          inv.items.forEach(item => {
            if (item.allocated > 0) {
              if (inv.id === 'opening_arrears' || item.id === 'opening_arrears_item') {
                allocations.push({ invoice_id: 'opening_arrears', amount: item.allocated });
              } else if (item.id === 'penalty') {
                allocations.push({ invoice_id: inv.id, amount: item.allocated });
              } else {
                allocations.push({ invoice_id: inv.id, invoice_item_id: item.id, amount: item.allocated });
              }
            }
          });
        } else {
          if (inv.allocated > 0) {
            allocations.push({ invoice_id: inv.id === 'opening_arrears' ? 'opening_arrears' : inv.id, amount: inv.allocated });
          }
        }
      });
      const res = await maintenanceService.settleDues(settleModal.id, {
        received_amount: receivedAmt,
        payment_mode: settleForm.payment_mode || 'UPI',
        reference_number: settleForm.reference_number || null,
        payment_date: settleForm.payment_date,
        allocations
      });
      const paymentData = res.data?.data || res.data;
      setSettleModal(null);
      fetchInvoiceDashboard();
      refreshData();

      const paymentId = paymentData?.id;
      if (paymentId) {
        try {
          await maintenanceService.downloadReceiptPdf(paymentId, paymentData?.reference_number || `REC-${paymentId}`);
          toastSuccess('Payment settled and receipt generated successfully!');
        } catch (_) {
          toastSuccess('Payment settled successfully!');
        }
      } else {
        toastSuccess('Payment settled successfully!');
      }
    } catch (err) {
      let errMsg = null;
      if (err.errors && typeof err.errors === 'object') {
        const list = Object.values(err.errors).flat().filter(Boolean);
        if (list.length > 0) errMsg = list.join('; ');
      }
      if (!errMsg && err.response?.data?.errors && typeof err.response.data.errors === 'object') {
        const list = Object.values(err.response.data.errors).flat().filter(Boolean);
        if (list.length > 0) errMsg = list.join('; ');
      }
      if (!errMsg) {
        errMsg = err.error || err.response?.data?.error || err.message || err.response?.data?.message || 'Failed to settle dues. Please check invoice details and try again.';
      }
      toastError(errMsg);
    } finally {
      setSettleLoading(false);
    }
  };

  const openLedgerModal = async (unit) => {
    setLedgerModal(unit);
    setLedgerPage(1);
    setLedgerTab("all");
    setLedgerLoading(true);
    try {
      const res = await maintenanceService.getUnitLedger(unit.id, 1);
      setLedgerData(res.data?.data || res.data);
    } catch {
      toastError('Failed to load ledger.');
    } finally {
      setLedgerLoading(false);
    }
  };

  const fetchLedgerPage = async (pg) => {
    if (!ledgerModal) return;
    setLedgerPage(pg);
    setLedgerLoading(true);
    try {
      const res = await maintenanceService.getUnitLedger(ledgerModal.id, pg);
      setLedgerData(res.data?.data || res.data);
    } catch {
      toastError('Failed to load ledger.');
    } finally {
      setLedgerLoading(false);
    }
  };

  // Compute settle preview
  const settlePreview = (() => {
    if (!settleInvoices.length) return [];

    let manualSum = 0;
    Object.keys(settleOverrides).forEach(id => {
      manualSum += parseFloat(settleOverrides[id]) || 0;
    });

    let remaining = (parseFloat(settleForm.received_amount) || 0) - manualSum;

    return settleInvoices.map(inv => {
      const invBal = parseFloat(inv.balance_amount || 0);
      if (inv.items && inv.items.length > 0) {
        let invAlloc = 0;
        let mappedItems = inv.items.map(item => {
          const itemBal = parseFloat(item.balance_amount !== undefined && item.balance_amount !== null && !isNaN(parseFloat(item.balance_amount)) ? item.balance_amount : item.amount) || 0;
          let alloc = 0;
          const overrideKey = `item_${item.id}`;
          if (settleOverrides[overrideKey] !== undefined) {
            alloc = parseFloat(settleOverrides[overrideKey]) || 0;
          } else {
            alloc = Math.min(Math.max(remaining, 0), itemBal);
            remaining -= alloc;
          }
          invAlloc += alloc;
          return { ...item, balance_amount: itemBal, allocated: alloc, remaining_balance: itemBal - alloc };
        });

        const itemsBalanceSum = mappedItems.reduce((sum, item) => sum + (parseFloat(item.balance_amount) || 0), 0);
        const penaltyAmount = Math.max(0, invBal - itemsBalanceSum);
        if (penaltyAmount > 0) {
          let pAlloc = 0;
          const pOverrideKey = `item_penalty`;
          if (settleOverrides[pOverrideKey] !== undefined) {
            pAlloc = parseFloat(settleOverrides[pOverrideKey]) || 0;
          } else {
            pAlloc = Math.min(Math.max(remaining, 0), penaltyAmount);
            remaining -= pAlloc;
          }
          invAlloc += pAlloc;
          mappedItems.push({ id: 'penalty', description: 'Late Payment Penalty', balance_amount: penaltyAmount, allocated: pAlloc, remaining_balance: penaltyAmount - pAlloc });
        }

        const finalAlloc = Math.min(invAlloc, invBal > 0 ? invBal : invAlloc);
        return { ...inv, allocated: finalAlloc, items: mappedItems };
      } else {
        let alloc = 0;
        const overrideKey = `inv_${inv.id}`;
        if (settleOverrides[overrideKey] !== undefined) {
          alloc = parseFloat(settleOverrides[overrideKey]) || 0;
        } else {
          alloc = Math.min(Math.max(remaining, 0), invBal);
          remaining -= alloc;
        }
        return { ...inv, allocated: alloc, remaining_balance: invBal - alloc };
      }
    });
  })();

  const settleExcess = (() => {
    const allocatedSum = settlePreview.reduce((sum, inv) => sum + (inv.allocated || 0), 0);
    const amt = parseFloat(settleForm.received_amount) || 0;
    return Math.max(0, amt - allocatedSum);
  })();

  const isOverAllocated = (() => {
    const allocatedSum = settlePreview.reduce((sum, inv) => sum + (inv.allocated || 0), 0);
    const amt = parseFloat(settleForm.received_amount) || 0;
    return allocatedSum > amt;
  })();

  useEffect(() => {
    if (mainTab === 2) fetchInvoiceDashboard();
  }, [mainTab, fetchInvoiceDashboard]);

  return (
    <div className="fade-up space-y-4">
      {deleteUnitId && <ConfirmModal title={t("modal_delete_unit")} message={`Permanently delete unit ${unitDetailModal?.unit}? All payment history will be lost.`} onConfirm={deleteUnit} onCancel={() => setDeleteUnitId(null)} />}

      {receiptModal && (
        <Modal title={t("modal_payment_receipt")} onClose={() => setReceiptModal(null)}>
          <ReceiptCard unit={receiptModal.unit} entry={receiptModal.entry} t={t} />
          <div className="flex gap-2 mt-4 justify-end">
            <Btn sm loading={loadingReceiptId === 'modal'} disabled={loadingReceiptId === 'modal'} onClick={async () => {
              try {
                setLoadingReceiptId('modal');
                await maintenanceService.downloadReceiptPdf(receiptModal.entry.receipt_id, receiptModal.entry.receipt_no);
                toastSuccess(`Receipt ${receiptModal.entry?.receipt_no} downloaded.`);
                setReceiptModal(null);
              } catch (e) {
                toastError("Failed to download receipt");
              } finally {
                setLoadingReceiptId(null);
              }
            }} icon="download">{t("btn_download_receipt")}</Btn>
            <Btn sm onClick={() => window.print()} icon="eye">{t("btn_print")}</Btn>
            <Btn sm onClick={() => setReceiptModal(null)}>{t("btn_close")}</Btn>
          </div>
        </Modal>
      )}



      {addUnitModal && (
        <Modal title={t("btn_add_new_unit")} onClose={() => { setAddUnitModal(false); setAddUnitForm(DEF_UNIT_FORM); setApiErrors(null); }} wide>
          {billingDefaultsLoading && <Alert type="info">{t("msg_loading_billing_defaults")}</Alert>}
          <UnitFormBody form={auForm} setForm={setAuForm} ff={auf} errors={apiErrors} t={t} isEdit={false} existingUnitTypes={existingUnitTypes} onAddNewUnitType={handleAddNewUnitType} onDeleteUnitType={handleDeleteUnitType} />
          <FormActions onSave={saveAddUnit} loading={savingAddUnit} onCancel={() => { setAddUnitModal(false); setAddUnitForm(DEF_UNIT_FORM); setApiErrors(null); }} label={t("btn_add_unit")} />
        </Modal>
      )}

      {importModal && (
        <Modal title="Bulk Import Units" onClose={() => { setImportModal(false); setImportFile(null); setApiErrors(null); }}>
          <div className="space-y-4">
            <p className="text-sm text-muted">
              Download the sample sheet, fill in the unit details, and upload the CSV file here to bulk import or update units.
            </p>
            <div className="flex gap-2">
              <Btn sm icon="download" onClick={handleDownloadTemplate}>Download Sample Sheet</Btn>
            </div>
            {apiErrors?.errors?.general && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400 max-h-40 overflow-y-auto">
                <ul className="list-disc pl-4 space-y-1">
                  {apiErrors.errors.general.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5">Select CSV File</label>
              <input type="file" accept=".csv" onChange={e => setImportFile(e.target.files[0])} className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer border border-border bg-white dark:bg-slate-800" />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Btn onClick={() => { setImportModal(false); setImportFile(null); setApiErrors(null); }}>Cancel</Btn>
            <Btn variant="primary" icon="upload" onClick={handleImportSubmit} loading={importing} disabled={!importFile}>Upload & Process</Btn>
          </div>
        </Modal>
      )}

      {unitDetailModal && (
        <Modal title={`${unitDetailModal.unit} - ${unitDetailModal.owner}`} onClose={() => { setUnitDetailModal(null); setEditApiErrors(null); }} xl>
          <Tabs tabs={[t("tab_unit_overview"), t("tab_payment_history"), t("tab_arrears_advance"), ...(!isStrictMember ? [t("tab_edit_unit")] : [])]} active={unitDetailTab} onChange={(t) => { setUnitDetailTab(t); setEditApiErrors(null); }} />

          {unitDetailTab === 0 && (
            <div className="space-y-4 mt-3">
              <div className="flex items-start gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                <Avatar name={unitDetailModal.owner} size={52} />
                <div className="flex-1">
                  <div className="font-bold text-ink text-base">{unitDetailModal.owner}</div>
                  <div className="text-xs text-muted mt-0.5">{unitDetailModal.unit} · {unitDetailModal.floor} · {unitDetailModal.unit_type}</div>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    <Badge color={unitDetailModal.ownership_type === t("label_owner") ? "blue" : "purple"}>{unitDetailModal.ownership_type}</Badge>
                    <span className="text-xs text-muted">{unitDetailModal.phone}</span>
                    <span className="text-xs text-muted">{unitDetailModal.email}</span>
                  </div>
                  {unitDetailModal.ownership_type === "Tenant" && unitDetailModal.tenant_name && (
                    <div className="text-xs text-muted mt-1">Tenant: {unitDetailModal.tenant_name} · {unitDetailModal.tenant_phone}</div>
                  )}
                  {unitDetailModal.vehicles?.length > 0 && (
                    <div className="flex gap-1.5 mt-2 flex-wrap">
                      {unitDetailModal.vehicles.map((v, i) => <span key={i} className="text-sm font-mono bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded-full">{v}</span>)}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 border border-border rounded-xl">
                  <div className="text-xs font-bold text-muted uppercase tracking-wider mb-3">{t("form_bill_components")}</div>
                  {unitDetailModal.bill_components.map((c, i) => (
                    <div key={i} className="flex justify-between text-sm py-1 border-b border-slate-50 dark:border-slate-800 last:border-0">
                      <span className="text-muted">{c.head}</span><span className="font-semibold text-ink">₹{Number(c.amount).toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm pt-2 font-bold text-ink">
                    <span>{t("label_total")}</span><span>₹{unitDetailModal.monthly_bill.toLocaleString()}</span>
                  </div>
                </div>
                <div className="p-4 border border-border rounded-xl">
                  <div className="text-xs font-bold text-muted uppercase tracking-wider mb-3">{t("label_current_status")}</div>
                  {[["Status", <Badge color={unitDetailModal.status === "Paid" ? "green" : unitDetailModal.status === "Partial" ? "amber" : "red"} dot>{unitDetailModal.status}</Badge>], ["Billed", `₹${unitDetailModal.monthly_bill.toLocaleString()}`], ["Paid", <span className="text-emerald-600 dark:text-emerald-500 font-semibold">₹{unitDetailModal.paid.toLocaleString()}</span>], ["Balance", <span className="text-red-500 dark:text-red-400 font-semibold">{unitDetailModal.monthly_bill - unitDetailModal.paid > 0 ? `₹${(unitDetailModal.monthly_bill - unitDetailModal.paid).toLocaleString()}` : t("label_nil")}</span>], ...(unitDetailModal.penalties_due > 0 ? [["Penalties Due", <span className="text-red-600 font-bold">₹{unitDetailModal.penalties_due.toLocaleString()}</span>]] : []), ...(unitDetailModal.advance_balance > 0 ? [["Advance", <span className="text-blue-600 font-semibold">₹{unitDetailModal.advance_balance.toLocaleString()}</span>]] : []), ...(unitDetailModal.arrears > 0 ? [[t("label_arrears"), <span className="text-red-600 font-semibold">₹{unitDetailModal.arrears.toLocaleString()}</span>]] : [])].map(([l, v]) => (
                    <div key={l} className="flex justify-between text-sm py-1 border-b border-slate-50 dark:border-slate-800 last:border-0">
                      <span className="text-muted">{l}</span><span>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {!isStrictMember && (
                  <>
                    <Btn sm variant="primary" icon="plus" onClick={() => { setUnitDetailModal(null); openSettleModal(unitDetailModal); }}>{t("btn_record_payment")}</Btn>
                    <Btn sm icon="plus" onClick={() => { setCustomInvoiceModal(unitDetailModal); setUnitDetailModal(null); }}>Add Custom Charge</Btn>
                  </>
                )}
                <Btn sm icon="download" loading={loadingReceiptId === unitDetailModal.id} onClick={async () => {
                  const last = unitDetailModal.history?.[0];
                  if (last && (last.receipt_id || last.id)) {
                    try {
                      setLoadingReceiptId(unitDetailModal.id);
                      await maintenanceService.downloadReceiptPdf(last.receipt_id || last.id, last.receipt_no || `REC-${last.id}`);
                      toastSuccess(`Receipt downloaded for ${unitDetailModal.unit}.`);
                    } catch (e) {
                      toastError("Failed to download receipt.");
                    } finally {
                      setLoadingReceiptId(null);
                    }
                  } else if (unitDetailModal.invoices && unitDetailModal.invoices.length > 0) {
                    try {
                      setLoadingReceiptId(unitDetailModal.id);
                      const latestInv = unitDetailModal.invoices[0];
                      await maintenanceService.downloadInvoicePdf(latestInv.id, latestInv.invoice_number);
                      toastSuccess(`Receipt / Bill downloaded for ${unitDetailModal.unit}.`);
                    } catch (e) {
                      toastError("Failed to download receipt.");
                    } finally {
                      setLoadingReceiptId(null);
                    }
                  } else {
                    toastError("No receipt available to download.");
                  }
                }}>{t("btn_generate_receipt")}</Btn>
                {!isStrictMember && (
                  <>
                    <Btn sm icon="mail" loading={remindingUnitId === unitDetailModal.id} onClick={() => handleSingleReminder(unitDetailModal)}>{t("btn_send_reminder")}</Btn>
                    <Btn sm onClick={() => setUnitDetailTab(3)}>{t("btn_edit_unit")}</Btn>
                  </>
                )}
              </div>
            </div>
          )}

          {unitDetailTab === 1 && (
            <div className="space-y-4 mt-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-semibold text-muted">{t("label_financial_year")}</label>
                  <select
                    value={detailFY}
                    onChange={e => { setDetailFY(e.target.value); setSelectedMonthEntry(null); }}
                    className="px-3 py-1.5 border border-border rounded-lg text-xs font-semibold text-ink dark:text-slate-200 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
                  >
                    {getAvailableFYs().map(fy => <option key={fy} value={fy}>FY {fy}</option>)}
                  </select>
                </div>
                <Btn sm icon="download" loading={loadingStatement} onClick={async () => { try { setLoadingStatement(true); await maintenanceService.downloadStatementPdf(unitDetailModal.id, detailFY, unitDetailModal.unit); toastSuccess(`Statement downloaded for ${unitDetailModal.unit} - FY ${detailFY}.`); } catch (e) { toastError(e?.message || "Failed to download statement."); } finally { setLoadingStatement(false); } }}>{t("btn_download_statement")}</Btn>
              </div>
              <div>
                <div className="text-xs font-semibold text-muted mb-2">12-Month Overview - FY {detailFY}</div>
                <div className="grid grid-cols-6 sm:grid-cols-12 gap-1.5">
                  {getMonthsForFY(detailFY).map((month, i) => {
                    const entry = unitDetailModal.history.find(h => h.fy === detailFY && h.month === month);
                    const invoice = unitDetailModal.invoices?.find(inv => {
                      if (!inv.billing_month) return false;
                      const d = new Date(inv.billing_month);
                      const mStr = d.toLocaleString('en-US', { month: 'short' }) + ' ' + d.getFullYear();
                      return mStr === month;
                    });
                    const status = invoice ? (invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)) : (entry ? (entry.amount >= entry.bill_amount ? "Paid" : "Partial") : "Unpaid");
                    const isSelected = selectedMonthEntry?.month === month;
                    const hasData = entry || invoice;

                    const moveInDate = unitDetailModal.move_in_date ? new Date(unitDetailModal.move_in_date) : null;
                    if (moveInDate) {
                      moveInDate.setDate(1);
                      moveInDate.setHours(0, 0, 0, 0);
                    }
                    const monthDate = new Date(month);
                    const isBeforeMoveIn = moveInDate && monthDate < moveInDate;

                    return (
                      <button key={i} disabled={isBeforeMoveIn} onClick={() => hasData && setSelectedMonthEntry(isSelected ? null : { month, entry, invoice })} className={`p-1.5 rounded-lg border text-center transition-all ${isSelected ? "border-primary bg-primary/10" : "border-border bg-white dark:bg-slate-800 hover:border-primary/30"} ${!hasData && !isBeforeMoveIn ? "opacity-50" : ""} ${isBeforeMoveIn ? "opacity-30 cursor-not-allowed bg-slate-50 dark:bg-slate-900" : "cursor-pointer"}`}>
                        <div className="text-[9px] text-muted font-semibold">{month.split(' ')[0]}</div>
                        <MonthBadge t={t} status={status} title={invoice ? `₹${invoice.total_amount.toLocaleString()}` : (entry ? `₹${entry.amount.toLocaleString()} - ${entry.date}` : "Unpaid")} disabled={isBeforeMoveIn} />
                        {invoice && <div className="text-[8px] text-muted mt-0.5 truncate">{invoice.invoice_number}</div>}
                      </button>
                    );
                  })}
                </div>
                {selectedMonthEntry && (
                  <div className="mt-2 p-3 bg-primary/5 border border-primary/20 rounded-xl text-xs flex flex-col gap-3">
                    {selectedMonthEntry.invoice && (
                      <div className="flex items-center justify-between bg-white dark:bg-slate-800 p-2 rounded border border-border">
                        <div>
                          <span className="font-semibold text-ink">Invoice {selectedMonthEntry.invoice.invoice_number}</span>
                          <div className="text-sm text-muted">Billed: ₹{parseFloat(selectedMonthEntry.invoice.total_amount).toLocaleString()}</div>
                        </div>
                        <Btn xs icon="download" onClick={async () => {
                          try {
                            await maintenanceService.downloadInvoicePdf(selectedMonthEntry.invoice.id, selectedMonthEntry.invoice.invoice_number);
                            toastSuccess("Invoice downloaded.");
                          } catch (e) {
                            toastError("Failed to download invoice.");
                          }
                        }}>{t("btn_download_invoice")}</Btn>
                      </div>
                    )}

                    {selectedMonthEntry.entry ? (
                      <div className="flex items-center justify-between bg-white dark:bg-slate-800 p-2 rounded border border-border">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 flex-1">
                          {[[t("label_month"), selectedMonthEntry.entry.month], ["Amount Paid", `₹${selectedMonthEntry.entry.amount.toLocaleString()}`], ["Mode", selectedMonthEntry.entry.mode], ["Ref", selectedMonthEntry.entry.ref || "-"], ["Date", selectedMonthEntry.entry.date], ["Received By", selectedMonthEntry.entry.received_by], ["Receipt No.", selectedMonthEntry.entry.receipt_no], ...(selectedMonthEntry.entry.notes ? [["Notes", selectedMonthEntry.entry.notes]] : [])].map(([l, v]) => (
                            <div key={l}><span className="text-muted">{l}: </span><strong>{v}</strong></div>
                          ))}
                        </div>
                        {(selectedMonthEntry.entry.receipt_id || selectedMonthEntry.entry.id) && (
                          <Btn xs icon="download" onClick={async () => {
                            try {
                              await maintenanceService.downloadReceiptPdf(selectedMonthEntry.entry.receipt_id || selectedMonthEntry.entry.id, selectedMonthEntry.entry.receipt_no || `REC-${selectedMonthEntry.entry.id}`);
                              toastSuccess("Receipt downloaded.");
                            } catch (e) {
                              toastError("Failed to download receipt.");
                            }
                          }}>{t("btn_download_receipt")}</Btn>
                        )}
                      </div>
                    ) : (
                      <div className="text-muted text-center py-2">{t("msg_no_payments_recorded")}</div>
                    )}
                  </div>
                )}
              </div>
              <DataTable
                cols={[t("label_month"), "FY", "Bill Amt", "Paid", "Mode", "Ref / Txn", "Receipt No.", "Received By", "Status", "Action"]}
                rows={unitDetailModal.history.filter(h => h.fy === detailFY).map(h => [
                  h.month, h.fy,
                  `₹${h.bill_amount.toLocaleString()}`,
                  <span className={h.amount >= h.bill_amount ? "text-emerald-600 dark:text-emerald-500 font-semibold" : "text-amber-600 font-semibold"}>₹{h.amount.toLocaleString()}</span>,
                  h.mode, <span className="text-sm font-mono">{h.ref || "-"}</span>,
                  <span className="text-sm font-mono text-primary">{h.receipt_no}</span>,
                  h.received_by,
                  <Badge color={h.amount >= h.bill_amount ? "green" : "amber"} dot>{h.amount >= h.bill_amount ? "Paid" : "Partial"}</Badge>,
                  (h.receipt_id || h.id) ? (
                    <Btn xs icon="download" onClick={async () => {
                      try {
                        await maintenanceService.downloadReceiptPdf(h.receipt_id || h.id, h.receipt_no || `REC-${h.id}`);
                        toastSuccess("Receipt downloaded.");
                      } catch (e) {
                        toastError("Failed to download receipt.");
                      }
                    }}>{t("label_receipt")}</Btn>
                  ) : null
                ])}
              />
            </div>
          )}

          {unitDetailTab === 2 && (
            <div className="space-y-4 mt-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 border border-red-100 dark:border-red-950/20 bg-red-50/40 dark:bg-red-950/20 rounded-xl">
                  <div className="text-xs font-bold text-red-400 uppercase tracking-wider mb-2">{t("label_arrears")}</div>
                  <div className="text-2xl font-black text-red-600 mb-1">₹{unitDetailModal.arrears.toLocaleString()}</div>
                  <div className="text-xs text-muted mb-3">{t("label_outstanding_balance_carry_forward")}</div>
                  {/* {unitDetailModal.arrears > 0 && (
                    <div className="text-xs text-red-500 dark:text-red-400 mb-3">
                      Penalty @ {billingConfig?.late_payment_fee || 0}% / month = <strong>₹{Math.round(unitDetailModal.arrears * ((billingConfig?.late_payment_fee || 0) / 100)).toLocaleString()}/month</strong>
                    </div>
                  )} */}
                  {!isStrictMember && (
                    <Btn sm variant="danger" onClick={() => toastSuccess(`Demand notice generated for ${unitDetailModal.unit} - ₹${unitDetailModal.arrears.toLocaleString()}.`)}>{t("btn_generate_demand_notice")}</Btn>
                  )}
                </div>
                <div className="p-4 border border-blue-100 dark:border-blue-950/20 bg-blue-50/40 dark:bg-blue-950/20 rounded-xl">
                  <div className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-2">{t("label_advance_balance")}</div>
                  <div className="text-2xl font-black text-blue-600 mb-1">₹{unitDetailModal.advance_balance.toLocaleString()}</div>
                  <div className="text-xs text-muted">{t("msg_adjusted_upcoming")}</div>
                </div>
              </div>
              <div>
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{t("label_unpaid_months")}</div>
                {(() => {
                  const moveInDate = unitDetailModal.move_in_date ? new Date(unitDetailModal.move_in_date) : null;
                  if (moveInDate) {
                    moveInDate.setDate(1);
                    moveInDate.setHours(0, 0, 0, 0);
                  }

                  const now = new Date();
                  now.setDate(1);
                  now.setHours(0, 0, 0, 0);

                  const unpaidMonths = [];
                  getAvailableFYs().forEach(fy => {
                    getMonthsForFY(fy).forEach(month => {
                      const monthDate = new Date(month);

                      if (moveInDate && monthDate < moveInDate) return;
                      if (monthDate > now) return;

                      const entry = unitDetailModal.history.find(h => h.fy === fy && h.month === month);
                      const invoice = unitDetailModal.invoices?.find(inv => {
                        if (!inv.billing_month) return false;
                        const d = new Date(inv.billing_month);
                        const mStr = d.toLocaleString('en-US', { month: 'short' }) + ' ' + d.getFullYear();
                        return mStr === month;
                      });

                      const isUnpaid = invoice ? invoice.status === 'unpaid' : !entry;

                      if (isUnpaid) {
                        unpaidMonths.push({ fy, month });
                      }
                    });
                  });

                  return unpaidMonths.length === 0
                    ? <p className="text-sm text-muted">{t("msg_no_unpaid_months")}</p>
                    : <div className="flex flex-wrap gap-1.5">{unpaidMonths.map((m, i) => <Badge key={i} color="red">{m.month} ({m.fy})</Badge>)}</div>;
                })()}
              </div>
            </div>
          )}

          {unitDetailTab === 3 && editUnitForm && (
            <div className="mt-3">
              <UnitFormBody form={editUnitForm} setForm={setEuForm} ff={euf} errors={editApiErrors} t={t} isEdit={true} existingUnitTypes={existingUnitTypes} onAddNewUnitType={handleAddNewUnitType} onDeleteUnitType={handleDeleteUnitType} />
              <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                <Btn variant="primary" icon="check" loading={savingEditUnit} onClick={saveEditUnit}>{t("btn_save_changes")}</Btn>
                <Btn onClick={() => { setUnitDetailTab(0); setEditApiErrors(null); }}>{t("btn_cancel")}</Btn>
                <Btn variant="danger" icon="trash" className="ml-auto" onClick={() => setDeleteUnitId(unitDetailModal.id)}>{t("btn_delete_unit")}</Btn>
              </div>
            </div>
          )}
        </Modal>
      )}


      {!isStrictMember && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <MetricCard label={t("stat_total_units")} value={stats?.total_units || units.length} icon="building" accent="blue" sublabel={t("sub_registered")} />
          <MetricCard label={t("stat_paid_this_month")} value={stats?.paid_this_month || 0} delta={t("sub_payments_received")} deltaUp icon="check" accent="green" />
          <MetricCard label={t("stat_outstanding_units")} value={stats?.outstanding_units || 0} delta={t("sub_units_pending")} deltaUp={false} icon="alert" accent="red" />
          <MetricCard label={t("stat_amount_collected")} value={`₹${((stats?.amount_collected || 0) / 1000).toFixed(1)}k`} delta={t("sub_total_received")} deltaUp icon="money" accent="amber" />
          <MetricCard label={t("stat_arrears_pending")} value={`₹${((stats?.arrears_pending || 0) / 1000).toFixed(1)}k`} delta={t("sub_carry_forward")} deltaUp={false} icon="alert" accent="red" className="col-span-2 sm:col-span-1" />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center w-full sm:w-auto">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted">
              <Icon name="filter" size={14} />
            </div>
            <input type="text" value={blockFilter} onChange={e => { setBlockFilter(e.target.value); setPage(1); }} placeholder="Filter Block..." className="pl-8 pr-3 py-1.5 border border-border rounded-lg text-sm bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 w-32 sm:w-40 placeholder:text-slate-400" />
          </div>
          <SearchBar value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder={t("search_placeholder")} className="w-full sm:w-56" />
          <div className="flex gap-1 overflow-x-auto hide-scrollbar pb-1 sm:pb-0" style={{ WebkitOverflowScrolling: 'touch' }}>
            {["All", "Paid", "Partial", "Unpaid"].map(s => (
              <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer border-0 transition-all ${statusFilter === s ? s === "Paid" ? "bg-emerald-500 text-white" : s === "Partial" ? "bg-amber-500 text-white" : s === "Unpaid" ? "bg-red-500 text-white" : "bg-ink dark:bg-primary text-white" : "bg-slate-100 dark:bg-slate-800 text-muted dark:text-slate-400 hover:text-ink dark:hover:text-slate-200"}`}>{t(`filter_${s.toLowerCase()}`)}</button>
            ))}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap mt-2 sm:mt-0">
          {canCreate && (
            unitLimit !== -1 && totalRecords >= unitLimit ? (
              <>
                <Btn sm variant="primary" icon="plus" onClick={() => toastError(`Unit limit reached (${unitLimit}/${unitLimit}). Please upgrade your plan.`)} className="hidden sm:inline-flex opacity-50 grayscale">{t("btn_add_unit")}</Btn>
                <Btn sm icon="upload" onClick={() => toastError(`Unit limit reached. Please upgrade your plan.`)} className="hidden sm:inline-flex opacity-50 grayscale">Bulk Import</Btn>
              </>
            ) : (
              <>
                <Btn sm variant="primary" icon="plus" onClick={() => setAddUnitModal(true)} className="hidden sm:inline-flex">{t("btn_add_unit")}</Btn>
                <Btn sm icon="upload" onClick={() => setImportModal(true)} className="hidden sm:inline-flex">Bulk Import</Btn>
              </>
            )
          )}
          {/* Generate bills button hidden per request */}
          {canRemind && <Btn sm icon="mail" loading={loadingReminders} onClick={async () => { try { setLoadingReminders(true); const res = await maintenanceService.sendReminders([], blockFilter); toastSuccess(`${res.data?.sent ?? 0} reminders dispatched.`); } catch (err) { toastError(err?.message || 'Failed to send reminders.'); } finally { setLoadingReminders(false); } }}>{t("btn_reminders")}</Btn>}
          {canExport && (
            <div className="relative" ref={exportRef}>
              <Btn sm icon="download" loading={loadingExport} onClick={() => setExportPopover(p => !p)}>{t("btn_export")}</Btn>
              {exportPopover && (
                <div className="absolute right-0 top-full mt-1 w-44 bg-white dark:bg-slate-900 border border-border rounded-xl shadow-lg z-50 overflow-hidden">
                  {[{ label: t('export_csv'), fmt: 'csv' }, { label: t('export_pdf'), fmt: 'pdf' }, { label: t('export_xlsx'), fmt: 'xlsx' }].map(opt => (
                    <button key={opt.label} onClick={async () => { setExportPopover(false); try { setLoadingExport(true); await maintenanceService.exportData(yearlyViewFY, opt.fmt); toastSuccess(`${opt.label} ready.`); } catch { toastError('Export failed.'); } finally { setLoadingExport(false); } }} className="w-full text-left px-4 py-2.5 text-sm text-ink hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer bg-transparent border-0 block">{opt.label}</button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {!isStrictMember && (
        <Tabs tabs={[t("tab_monthly"), t("tab_yearly")]} active={mainTab} onChange={setMainTab} />
      )}

      {mainTab === 0 && (
        <div className="xl:bg-white xl:dark:bg-slate-900 xl:border xl:border-border xl:dark:border-slate-700/60 xl:rounded-2xl xl:shadow-sm xl:p-5">
          {!isStrictMember && (
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="w-full sm:w-1/2 md:w-1/3">
                <ProgressBar value={totalCollected} max={totalBill} showPct label={`₹${totalCollected.toLocaleString()} collected of ₹${totalBill.toLocaleString()}`} />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs font-semibold text-muted">FY</label>
                <select
                  value={monthlyViewFY}
                  onChange={e => setMonthlyViewFY(e.target.value)}
                  className="px-3 py-1.5 border border-border rounded-lg text-sm font-bold text-ink bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
                >
                  {getAvailableFYs().map(fy => <option key={fy} value={fy}>FY {fy}</option>)}
                </select>
              </div>
            </div>
          )}
          <div className="hidden xl:block overflow-x-auto">
            <table className="w-full text-xs min-w-max">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-800">
                  <th className="px-3 py-2 text-left">
                    {!isStrictMember && (
                      <input type="checkbox" checked={selectedRows.size === units.length && units.length > 0} onChange={toggleAll} className="cursor-pointer" />
                    )}
                  </th>
                  {["Block", t("label_unit"), "Owner / Tenant", "Floor", "Bill Amt", "Advance (CF)", "Prev Dues", "Paid", "Balance", "Mode", "Status", "Actions"].map(c => (
                    <th key={c} className="px-2 py-3 text-left font-semibold text-muted whitespace-nowrap">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [1, 2, 3].map(i => (
                    <tr key={i} className="border-b border-slate-50 dark:border-slate-800/50">
                      <td colSpan={13} className="px-3 py-4">
                        <div className="skeleton h-10 w-full rounded-xl" />
                      </td>
                    </tr>
                  ))
                ) : units.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="py-16 text-center text-muted text-sm border-b border-transparent">
                      {search ? "No units found matching your search." : "No units found. Click '+ Add Unit' to get started."}
                    </td>
                  </tr>
                ) : units.map(u => (
                  <tr key={u.id} className={`border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50/60 dark:hover:bg-slate-800/40 group transition-colors ${selectedRows.has(u.id) ? "bg-primary/5 dark:bg-primary/10" : ""}`}>
                    <td className="px-3 py-2.5">
                      {!isStrictMember && (
                        <input type="checkbox" checked={selectedRows.has(u.id)} onChange={() => toggleRow(u.id)} className="cursor-pointer" />
                      )}
                    </td>
                    <td className="px-2 py-2.5 text-muted whitespace-nowrap">{u.block !== '-' ? u.block : ''}</td>
                    <td className="px-2 py-2.5">
                      <button onClick={() => openUnitDetail(u, 0)} className="text-left bg-transparent border-0 cursor-pointer p-0">
                        <div className="font-mono font-bold text-ink text-xs bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded hover:bg-primary/10 transition-colors">{u.unit}</div>
                        <div className="text-[9px] text-muted mt-0.5">{u.unit_type}</div>
                      </button>
                    </td>
                    <td className="px-2 py-2.5">
                      <button onClick={() => openUnitDetail(u, 0)} className="flex items-center gap-2 hover:text-primary transition-colors bg-transparent border-0 cursor-pointer p-0">
                        <Avatar name={u.owner} size={26} />
                        <div className="text-left">
                          <div className="text-xs font-semibold text-ink hover:text-primary">{u.owner}</div>
                          <div className="text-sm text-muted">{u.phone}</div>
                          <Badge color={u.ownership_type === t("label_owner") ? "blue" : "purple"} sm>{u.ownership_type}</Badge>
                        </div>
                      </button>
                    </td>
                    <td className="px-2 py-2.5 text-muted whitespace-nowrap">{u.floor}</td>
                    <td className="px-2 py-2.5 font-semibold dark:text-white">₹{u.monthly_bill.toLocaleString()}</td>
                    <td className="px-2 py-2.5">{u.opening_advance_fy > 0 ? <span className="text-emerald-600 dark:text-emerald-500 font-semibold">₹{u.opening_advance_fy.toLocaleString()}</span> : <span className="text-muted">-</span>}</td>
                    <td className="px-2 py-2.5">{u.opening_arrears_fy > 0 ? <span className="text-red-500 dark:text-red-400 font-semibold">₹{u.opening_arrears_fy.toLocaleString()}</span> : <span className="text-muted">-</span>}</td>
                    <td className="px-2 py-2.5"><span className={u.paid > 0 ? "text-emerald-600 dark:text-emerald-500 font-semibold" : "text-muted"}>{u.paid > 0 ? `₹${u.paid.toLocaleString()}` : "-"}</span></td>
                    <td className="px-2 py-2.5"><span className={u.monthly_bill + u.opening_arrears_fy - u.paid - u.opening_advance_fy > 0 ? "text-red-500 dark:text-red-400 font-semibold" : "text-muted"}>{u.monthly_bill + u.opening_arrears_fy - u.paid - u.opening_advance_fy > 0 ? `₹${(u.monthly_bill + u.opening_arrears_fy - u.paid - u.opening_advance_fy).toLocaleString()}` : t("label_nil")}</span></td>
                    <td className="px-2 py-2.5 text-muted">{u.last_payment_mode || '-'}</td>
                    <td className="px-2 py-2.5"><Badge color={u.status === "Paid" ? "green" : u.status === "Partial" ? "amber" : "red"} dot>{u.status}</Badge></td>
                    <td className="px-2 py-2.5">
                      <div className="flex gap-1 items-center flex-wrap">
                        {u.status !== "Paid" && !isStrictMember && (
                          <Btn sm variant="primary" icon="check" onClick={() => openSettleModal(u)}>{t("btn_settle_dues")}</Btn>
                        )}
                        <Btn sm icon="eye" onClick={() => openLedgerModal(u)}>{t("btn_history")}</Btn>
                        {!isStrictMember && (
                          <>
                            <Btn sm icon="edit" onClick={() => openUnitDetail(u, 3)}>{t("btn_edit")}</Btn>
                            {u.status !== "Paid" && <Btn sm icon="mail" loading={remindingUnitId === u.id} onClick={() => handleSingleReminder(u)}>{t("btn_remind")}</Btn>}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile view (cards) */}
          <div className="block xl:hidden space-y-4 px-1 pb-4 mt-4">
            {units.length > 0 && (
              <div className="flex items-center justify-center gap-1.5 text-[10px] text-muted mb-2 font-medium bg-slate-50 dark:bg-slate-800/50 py-1.5 rounded-full w-max mx-auto px-4">
                <Icon name="chevron_left" size={12} />
                <span>Swipe cards left for options</span>
              </div>
            )}
            {loading ? (
              [1, 2, 3].map(i => (
                <div key={i} className="border border-border rounded-xl p-4 bg-white dark:bg-slate-900 shadow-sm animate-pulse">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 w-1/3 mb-4 rounded"></div>
                  <div className="h-10 bg-slate-200 dark:bg-slate-700 w-full rounded"></div>
                </div>
              ))
            ) : units.length === 0 ? (
              <div className="py-16 text-center text-muted text-sm bg-white dark:bg-slate-900 rounded-xl border border-border">
                {search ? "No units found matching your search." : "No units found. Click '+ Add Unit' to get started."}
              </div>
            ) : units.map((u, index) => {
              const balanceDue = u.monthly_bill + u.opening_arrears_fy - u.paid - u.opening_advance_fy;
              return (
                <IonItemSliding key={u.id} className="mb-4 overflow-hidden rounded-xl shadow-sm border border-border">
                  <IonItem lines="none" className="ion-no-padding" style={{ '--background': 'transparent', '--padding-start': '0', '--inner-padding-end': '0' }}>
                    <div className={`w-full bg-white dark:bg-slate-900 p-4 relative transition-colors ${selectedRows.has(u.id) ? "border-primary/50 ring-1 ring-primary/20 bg-primary/5" : ""} ${index === 0 ? "animate-swipe-hint" : ""}`}>
                      {/* Swipe Hint Indicator */}
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center justify-center h-full px-1.5 opacity-30 pointer-events-none">
                        <Icon name="chevron_left" size={16} />
                      </div>

                      {!isStrictMember && (
                        <div className="absolute top-4 right-4">
                          <input type="checkbox" checked={selectedRows.has(u.id)} onChange={() => toggleRow(u.id)} className="cursor-pointer scale-125" />
                        </div>
                      )}

                      <div className="flex items-center gap-3 mb-4 cursor-pointer" onClick={() => openUnitDetail(u, 0)}>
                        <div className="px-3 min-w-[3.5rem] h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex flex-col items-center justify-center font-bold text-ink shrink-0">
                          <span className="text-sm">{u.unit}</span>
                        </div>
                        <div>
                          <div className="text-sm font-bold text-ink">{u.owner}</div>
                          <div className="text-xs text-muted">{u.phone}</div>
                        </div>
                      </div>

                      <div className="flex gap-2 mb-4 flex-wrap items-center">
                        <Badge color={u.ownership_type === t("label_owner") ? "blue" : "purple"} sm>{u.ownership_type}</Badge>
                        <Badge color={u.status === "Paid" ? "green" : u.status === "Partial" ? "amber" : "red"} sm dot>{u.status}</Badge>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-muted ml-auto bg-slate-50 dark:bg-slate-800 px-2 py-1 rounded">Floor {u.floor}</span>
                      </div>

                      <div className="grid grid-cols-2 gap-y-3 gap-x-2 text-xs bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-100 dark:border-slate-700/50">
                        <div>
                          <div className="text-muted font-semibold mb-1">Bill Amt</div>
                          <div className="font-bold text-ink">₹{u.monthly_bill.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-muted font-semibold mb-1">Paid</div>
                          <div className={`font-bold ${u.paid > 0 ? 'text-emerald-600 dark:text-emerald-500' : 'text-slate-400'}`}>{u.paid > 0 ? `₹${u.paid.toLocaleString()}` : "-"}</div>
                        </div>
                        <div>
                          <div className="text-muted font-semibold mb-1">Advance</div>
                          <div className={`font-bold ${u.opening_advance_fy > 0 ? 'text-emerald-600 dark:text-emerald-500' : 'text-slate-400'}`}>{u.opening_advance_fy > 0 ? `₹${u.opening_advance_fy.toLocaleString()}` : "-"}</div>
                        </div>
                        <div>
                          <div className="text-muted font-semibold mb-1">Balance</div>
                          <div className={`font-bold ${balanceDue > 0 ? 'text-red-500 dark:text-red-400' : 'text-slate-400'}`}>{balanceDue > 0 ? `₹${balanceDue.toLocaleString()}` : t("label_nil")}</div>
                        </div>
                      </div>
                    </div>
                  </IonItem>

                  <IonItemOptions side="end">
                    {u.status !== "Paid" && !isStrictMember && (
                      <IonItemOption color="success" onClick={() => openSettleModal(u)}>
                        <div className="flex flex-col items-center justify-center gap-1 w-16">
                          <Icon name="check" size={20} />
                          <span className="text-[10px] font-semibold">{t("btn_settle_dues")}</span>
                        </div>
                      </IonItemOption>
                    )}
                    <IonItemOption color="warning" onClick={() => openLedgerModal(u)}>
                      <div className="flex flex-col items-center justify-center gap-1 w-16">
                        <Icon name="eye" size={20} />
                        <span className="text-[10px] font-semibold">{t("btn_history")}</span>
                      </div>
                    </IonItemOption>
                    {!isStrictMember && (
                      <IonItemOption color="primary" onClick={() => openUnitDetail(u, 3)}>
                        <div className="flex flex-col items-center justify-center gap-1 w-16">
                          <Icon name="edit" size={20} />
                          <span className="text-[10px] font-semibold">{t("btn_edit")}</span>
                        </div>
                      </IonItemOption>
                    )}
                  </IonItemOptions>
                </IonItemSliding>
              );
            })}
          </div>
          <Pagination page={page} total={totalRecords} perPage={PER_PAGE} onPage={setPage} />
        </div>
      )}

      {mainTab === 1 && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-slate-900 border border-border rounded-2xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3 shrink-0">
              <label className="text-xs font-semibold text-muted whitespace-nowrap">{t("label_financial_year")}</label>
              <select
                value={yearlyViewFY}
                onChange={e => setYearlyViewFY(e.target.value)}
                className="px-3 py-1.5 border border-border rounded-lg text-sm font-bold text-ink bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
              >
                {getAvailableFYs().map(fy => <option key={fy} value={fy}>FY {fy}</option>)}
              </select>
            </div>
            <div className="h-px sm:h-8 sm:w-px bg-border shrink-0" />
            <div className="flex flex-wrap gap-x-6 gap-y-3 flex-1">
              {[
                { label: t("stat_yearly_billed"), value: `₹${(fyTotalBilled / 100000).toFixed(1)}L`, color: "text-blue-600" },
                { label: t("stat_yearly_collected"), value: `₹${(fyTotalCollected / 100000).toFixed(1)}L`, color: "text-emerald-600 dark:text-emerald-500" },
                { label: t("stat_yearly_outstanding"), value: `₹${(fyTotalPending / 1000).toFixed(0)}k`, color: "text-red-500 dark:text-red-400" },
                { label: t("stat_yearly_collection_pct"), value: `${fyCollectionPct}%`, color: fyCollectionPct >= 85 ? "text-emerald-600 dark:text-emerald-500" : "text-amber-500" },
                { label: t("stat_yearly_units_paid"), value: `${units.filter(u => u.status === "Paid").length}/${units.length}`, color: "text-ink" },
              ].map(s => (
                <div key={s.label} className="flex flex-col">
                  <span className="text-sm font-semibold text-muted uppercase tracking-wide">{s.label}</span>
                  <span className={`text-lg font-bold leading-tight ${s.color}`}>{s.value}</span>
                </div>
              ))}
            </div>
            <div className="shrink-0 w-full sm:w-32">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-muted font-semibold">{t("label_collection")}</span>
                <span className={`text-xs font-bold ${fyCollectionPct >= 85 ? "text-emerald-600 dark:text-emerald-500" : "text-amber-500"}`}>{fyCollectionPct}%</span>
              </div>
              <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${fyCollectionPct >= 85 ? "bg-emerald-500" : fyCollectionPct >= 70 ? "bg-amber-400" : "bg-red-400"}`} style={{ width: `${fyCollectionPct}%` }} />
              </div>
            </div>
          </div>

          <Card>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm font-bold text-ink">Unit-wise Summary - FY {yearlyViewFY}</div>
                <div className="text-xs text-muted mt-0.5">
                  <span className="text-emerald-600 dark:text-emerald-500 font-bold">✓</span> Paid &nbsp;
                  <span className="text-amber-500 font-bold">~</span> Partial &nbsp;
                  <span className="text-red-500 dark:text-red-400 font-bold">✗</span> Unpaid &nbsp;- click any row to view details
                </div>
              </div>
              <div className="flex gap-2">
                <Btn sm icon="download" onClick={async () => { try { await maintenanceService.exportData(yearlyViewFY, 'pdf'); toastSuccess(`FY ${yearlyViewFY} report download started.`); } catch (e) { toastError('Failed to download report.'); } }}>{t("btn_export_fy_report")}</Btn>
                <Btn sm icon="download" onClick={async () => { try { await maintenanceService.exportData(yearlyViewFY, 'csv'); toastSuccess("Export started."); } catch (e) { toastError('Failed to export.'); } }}>{t("btn_export_csv")}</Btn>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-max">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-slate-800">
                    <th className="text-left py-2 px-2 font-semibold text-muted whitespace-nowrap">{t("label_unit")}</th>
                    <th className="text-left py-2 px-2 font-semibold text-muted whitespace-nowrap">{t("label_owner")}</th>
                    {(fyMonthsShort.length ? fyMonthsShort : ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar']).map((m, i) => {
                      const isCurrentMonth = fyMonths[i] === currentMonthStr;
                      return (
                        <th key={m + i} className={`text-center py-2 px-1.5 font-semibold ${isCurrentMonth ? 'text-primary bg-primary/10 rounded-t-lg border-b-2 border-primary' : 'text-muted'}`}>
                          {m}
                        </th>
                      );
                    })}
                    <th className="text-right py-2 px-2 font-semibold text-muted whitespace-nowrap">{t("col_total_paid")}</th>
                    <th className="text-right py-2 px-2 font-semibold text-muted whitespace-nowrap">{t("col_outstanding")}</th>
                  </tr>
                </thead>
                <tbody>
                  {yearlyLoading ? (
                    <tr><td colSpan={15} className="text-center py-6 text-muted text-xs">{t("text_loading")}</td></tr>
                  ) : fyUnitWise.length === 0 ? (
                    <tr><td colSpan={15} className="text-center py-6 text-muted text-xs">{t("text_no_data")}</td></tr>
                  ) : fyUnitWise.map(u => (
                    <tr key={u.id} onClick={() => openUnitDetail({ id: u.id, unit: u.unit, owner: u.owner, monthly_bill: u.monthly_bill, floor: '-', unit_type: '-', ownership_type: 'Owner', phone: '', email: '', vehicles: [], bill_components: [], paid: u.total_paid, arrears: 0, advance_balance: 0, history: [] }, 1, yearlyViewFY)} className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50/60 dark:hover:bg-slate-800/40 cursor-pointer transition-colors">
                      <td className="py-2 px-2"><span className="font-mono font-bold text-ink bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-[11px]">{u.unit}</span></td>
                      <td className="py-2 px-2 font-medium text-ink whitespace-nowrap">{u.owner}</td>
                      {(u.statuses || Array(12).fill('Unpaid')).map((s, i) => {
                        const monthLabel = fyMonths[i];
                        let isDisabled = false;
                        if (monthLabel && u.move_in_date) {
                          const moveIn = new Date(u.move_in_date);
                          moveIn.setDate(1);
                          moveIn.setHours(0, 0, 0, 0);
                          const mDate = new Date(monthLabel);
                          if (mDate < moveIn) isDisabled = true;
                        }
                        return (
                          <td key={i} className={`text-center py-2 px-1.5 ${monthLabel === currentMonthStr ? 'bg-primary/5' : ''}`}>
                            <MonthBadge t={t} status={s} title={s} disabled={isDisabled} />
                          </td>
                        );
                      })}
                      <td className="text-right py-2 px-2 font-semibold text-emerald-600 dark:text-emerald-500">₹{Number(u.total_paid).toLocaleString()}</td>
                      <td className="text-right py-2 px-2 font-semibold text-red-500 dark:text-red-400">{u.outstanding > 0 ? `₹${Number(u.outstanding).toLocaleString()}` : <span className="text-muted">{t("label_nil")}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm font-bold text-ink">{t("label_financial_record")} {yearlyViewFY}</div>
                <div className="text-xs text-muted mt-0.5">{t("desc_month_wise_summary")}</div>
              </div>
            </div>
            {yearlyLoading ? (
              <div className="py-8 text-center text-muted text-xs">{t("msg_loading_monthly")}</div>
            ) : fyMonthly.length === 0 ? (
              <div className="py-8 text-center text-muted text-xs">{t("msg_no_billing_data").replace('{fy}', yearlyViewFY)}</div>
            ) : (
              <DataTable
                cols={[t("label_month"), t("col_bills_generated"), t("col_amount_billed"), t("col_amount_collected"), t("col_pending"), t("col_collection_pct")]}
                rows={fyMonthly.map(m => [
                  <span className="font-medium text-ink">{m.month}</span>,
                  m.bills,
                  `₹${Number(m.billed).toLocaleString()}`,
                  <span className="text-emerald-600 dark:text-emerald-500 font-semibold">₹{Number(m.collected).toLocaleString()}</span>,
                  <span className={m.pending > 0 ? "text-red-500 dark:text-red-400 font-semibold" : "text-muted"}>{m.pending > 0 ? `₹${Number(m.pending).toLocaleString()}` : t("label_nil")}</span>,
                  <Badge color={m.pct < 80 ? "amber" : "green"}>{m.pct}%</Badge>,
                ])}
              />
            )}
          </Card>
        </div>
      )}


      {customInvoiceModal && (
        <Modal title={`Add Custom Charge - ${customInvoiceModal.unit}`} onClose={() => { setCustomInvoiceModal(null); setCustomInvoiceForm({ description: '', amount: '', due_date: new Date().toISOString().slice(0, 10) }); }}>
          <div className="space-y-4 p-2">
            <Field label="Description" required placeholder="e.g. Penalty for late payment" value={customInvoiceForm.description} onChange={e => setCustomInvoiceForm(f => ({ ...f, description: e.target.value }))} />
            <FormRow cols={2}>
              <Field label="Amount" type="number" required placeholder="e.g. 500" value={customInvoiceForm.amount} onChange={e => setCustomInvoiceForm(f => ({ ...f, amount: e.target.value }))} />
              <Field label="Due Date" type="date" required value={customInvoiceForm.due_date} onChange={e => setCustomInvoiceForm(f => ({ ...f, due_date: e.target.value }))} />
            </FormRow>
          </div>
          <FormActions onSave={saveCustomInvoice} loading={customInvoiceLoading} onCancel={() => { setCustomInvoiceModal(null); setCustomInvoiceForm({ description: '', amount: '', due_date: new Date().toISOString().slice(0, 10) }); }} label="Add Charge" />
        </Modal>
      )}

      {settleModal && (
        <Modal title={`Settle Dues - ${settleModal.unit} (${settleModal.owner})`} onClose={() => setSettleModal(null)} xxl>
          <div className="space-y-6">
            {/* 1. Payment Information Section */}
            <div className="">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field
                  label={t("label_received_amount_rs")}
                  type="number"
                  required
                  placeholder="e.g. 5000"
                  value={settleForm.received_amount}
                  onChange={e => {
                    setSettleOverrides({});
                    setSettleForm(f => ({ ...f, received_amount: e.target.value }));
                  }}
                />
                <Field
                  label={t("settle_date")}
                  type="date"
                  required
                  value={settleForm.payment_date}
                  onChange={e => setSettleForm(f => ({ ...f, payment_date: e.target.value }))}
                />
                <Field
                  label={t("settle_mode")}
                  type="select"
                  options={["UPI", "Cash", "Bank Transfer", "Cheque"]}
                  value={settleForm.payment_mode}
                  onChange={e => setSettleForm(f => ({ ...f, payment_mode: e.target.value }))}
                />
                <Field
                  label={t("label_reference_no")}
                  tooltip="Txn ID / Cheque No"
                  placeholder="e.g. UTR or Cheque No"
                  value={settleForm.reference_number}
                  onChange={e => setSettleForm(f => ({ ...f, reference_number: e.target.value }))}
                />
              </div>
            </div>

            {/* 2. Allocation Summary Section */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/30 p-3 rounded-xl flex justify-between items-center">
                <span className="text-xs font-semibold uppercase tracking-wider">{t("label_received")}</span>
                <span className="text-lg font-bold">₹{Number(parseFloat(settleForm.received_amount) || 0).toLocaleString()}</span>
              </div>
              <div className="bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800/30 p-3 rounded-xl flex justify-between items-center">
                <span className="text-xs font-semibold uppercase tracking-wider">{t("label_allocated")}</span>
                <span className="text-lg font-bold">₹{Number(settlePreview.reduce((sum, inv) => sum + (inv.allocated || 0), 0)).toLocaleString()}</span>
              </div>
              <div className={`p-3 rounded-xl flex justify-between items-center border ${isOverAllocated ? 'bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800/30' : 'bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800/30'}`}>
                <span className="text-xs font-semibold uppercase tracking-wider">{isOverAllocated ? 'Over Allocated' : 'Remaining'}</span>
                <span className="text-lg font-bold">
                  ₹{Number(isOverAllocated ? Math.abs((parseFloat(settleForm.received_amount) || 0) - settlePreview.reduce((sum, inv) => sum + (inv.allocated || 0), 0)) : settleExcess).toLocaleString()}
                </span>
              </div>
            </div>

            {/* 3. Pending Invoice Allocation Section */}
            <div className="space-y-3">
              <div className="flex justify-between items-center flex-wrap gap-2">
                <div className="text-sm font-bold text-ink dark:text-slate-200">{t("label_pending_invoices")}</div>
                <button
                  type="button"
                  onClick={() => setSettleOverrides({})}
                  className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 text-xs font-semibold rounded-lg px-3 py-1.5 cursor-pointer transition-colors"
                >
                  <Icon name="sparkles" size={14} />
                  {t("label_auto_allocate")}
                </button>
              </div>

              {/* Desktop view (table) */}
              <div className="hidden md:block border border-border rounded-xl overflow-hidden bg-white dark:bg-slate-900 shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-max divide-y divide-border">
                    <thead className="bg-slate-100/50 dark:bg-slate-800 border-b border-border">
                      <tr>
                        <th className="w-10 px-4 py-3 text-left"></th>
                        <th className="px-4 py-3 text-left font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("label_invoice_number")}</th>
                        <th className="px-4 py-3 text-left font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("label_month")}</th>
                        <th className="px-4 py-3 text-right font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">{t("label_total_due")}</th>
                        <th className="px-4 py-3 text-right font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap w-48">{t("label_allocate_amount")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {settleInvoices.map((inv, i) => {
                        const preview = settlePreview[i];
                        const isExpanded = !!expandedInvoices[inv.id];
                        const hasItems = inv.items && inv.items.length > 0;

                        return (
                          <React.Fragment key={inv.id}>
                            {/* Invoice Main Row */}
                            <tr
                              className={`hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors ${hasItems ? 'cursor-pointer' : ''}`}
                              onClick={() => {
                                if (hasItems) setExpandedInvoices(prev => ({ ...prev, [inv.id]: !prev[inv.id] }));
                              }}
                            >
                              <td className="px-4 py-3 text-center">
                                {hasItems && (
                                  <button
                                    type="button"
                                    className="p-1 text-muted hover:text-ink bg-transparent border-0"
                                  >
                                    <Icon name={isExpanded ? "chevron_down" : "chevron_right"} size={14} />
                                  </button>
                                )}
                              </td>
                              <td className="px-4 py-3 font-semibold text-ink dark:text-slate-200">
                                {inv.invoice_number}
                              </td>
                              <td className="px-4 py-3 text-muted">
                                {new Date(inv.billing_month).toLocaleString('default', { month: 'short', year: 'numeric' })}
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-red-500 dark:text-red-400">
                                ₹{Number(inv.balance_amount).toLocaleString()}
                              </td>
                              <td className="px-4 py-3 text-right">
                                {hasItems ? (
                                  <span className="font-bold text-emerald-600 dark:text-emerald-500 pr-3">
                                    ₹{Number(preview?.allocated || 0).toLocaleString()}
                                  </span>
                                ) : (
                                  <input
                                    type="number"
                                    className="w-32 text-right text-sm font-medium px-3 py-1.5 border border-border rounded-lg bg-white dark:bg-slate-900 text-emerald-600 dark:text-emerald-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                                    value={settleOverrides[`inv_${inv.id}`] !== undefined ? settleOverrides[`inv_${inv.id}`] : (preview?.allocated || '')}
                                    onChange={e => handleOverrideChange(`inv_${inv.id}`, e.target.value, inv.balance_amount)}
                                    min="0"
                                    max={inv.balance_amount}
                                    placeholder="0"
                                  />
                                )}
                              </td>
                            </tr>

                            {/* Expandable breakdown row */}
                            {hasItems && isExpanded && (
                              <tr className="bg-slate-50/40 dark:bg-slate-800/20">
                                <td colSpan={5} className="px-4 py-1.5">
                                  <div className="pl-6 pr-2 py-1 border-l-2 border-slate-200 dark:border-slate-700/50">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500 mb-1">{t("label_itemized_breakdown")}</div>
                                    {preview?.items?.map(item => (
                                      <div key={item.id} className="flex justify-between items-center py-1 border-b last:border-0 border-slate-200 dark:border-slate-700/50 text-sm">
                                        <div className="flex items-center gap-5">
                                          <span className="">{item.description}</span>
                                          {item.id === 'penalty' && (
                                            <button
                                              onClick={async (e) => {
                                                e.stopPropagation();
                                                if (window.confirm(`Are you sure you want to waive the Late Payment Penalty of ₹${item.balance_amount} for this invoice?`)) {
                                                  try {
                                                    await maintenanceService.waivePenalty(inv.id);
                                                    toastSuccess('Penalty waived successfully.');
                                                    // Update modal state to remove penalty visually
                                                    setSettleInvoices(prev => prev.map(i => {
                                                      if (i.id === inv.id) {
                                                        const pItem = i.items.find(it => it.id === 'penalty');
                                                        const pAlloc = pItem ? (pItem.allocated || 0) : 0;
                                                        return {
                                                          ...i,
                                                          balance_amount: parseFloat(i.balance_amount) - item.balance_amount,
                                                          allocated: i.allocated - pAlloc,
                                                          items: i.items.filter(it => it.id !== 'penalty')
                                                        };
                                                      }
                                                      return i;
                                                    }));
                                                    refreshData();
                                                  } catch (err) {
                                                    toastError(err.response?.data?.message || 'Failed to waive penalty.');
                                                  }
                                                }
                                              }}
                                              className="text-sm font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full hover:bg-amber-200 dark:hover:bg-amber-800/60 transition-colors flex items-center gap-1 cursor-pointer"
                                              title="Click to completely waive this late fee penalty."
                                            >
                                              <Icon name="x" size={10} />
                                              Waive Penalty
                                            </button>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-4">
                                          <span className="font-semibold text-red-500 dark:text-red-400">Due: ₹{Number(item.balance_amount).toLocaleString()}</span>
                                          <div className="flex items-center gap-1.5">
                                            <span className="uppercase">Allocated:</span>
                                            <input
                                              type="number"
                                              className="w-32 text-right text-sm font-medium px-3 py-1.5 border border-border rounded-lg bg-white dark:bg-slate-900 text-emerald-600 dark:text-emerald-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                                              value={settleOverrides[`item_${item.id}`] !== undefined ? settleOverrides[`item_${item.id}`] : (item.allocated || '')}
                                              onChange={e => handleOverrideChange(`item_${item.id}`, e.target.value, item.balance_amount)}
                                              min="0"
                                              max={item.balance_amount}
                                              placeholder="0"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                      {settleInvoices.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center py-6 text-muted">{t("msg_no_pending_invoices")}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile view (cards) */}
              <div className="block md:hidden space-y-4">
                {settleInvoices.map((inv, i) => {
                  const preview = settlePreview[i];
                  const isExpanded = !!expandedInvoices[inv.id];
                  const hasItems = inv.items && inv.items.length > 0;

                  return (
                    <div key={inv.id} className="border border-border rounded-xl p-4 bg-white dark:bg-slate-900 shadow-sm space-y-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-bold text-sm text-ink dark:text-slate-200">{inv.invoice_number}</div>
                          <div className="text-xs text-muted">
                            {new Date(inv.billing_month).toLocaleString('default', { month: 'long', year: 'numeric' })}
                          </div>
                        </div>
                        <div className="text-right">
                          {/* <span className="text-sm text-muted uppercase block font-semibold">{t("label_total_due")}</span> */}
                          <span className="font-bold text-red-500 dark:text-red-400">₹{Number(inv.balance_amount).toLocaleString()}</span>
                        </div>
                      </div>

                      <div className="flex justify-between items-center pt-2 border-t border-border">
                        <span className="text-xs font-semibold text-muted">Allocated Amount:</span>
                        {hasItems ? (
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-emerald-600 dark:text-emerald-500 text-sm">
                              ₹{Number(preview?.allocated || 0).toLocaleString()}
                            </span>
                            <button
                              type="button"
                              onClick={() => setExpandedInvoices(prev => ({ ...prev, [inv.id]: !prev[inv.id] }))}
                              className="px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-semibold hover:bg-slate-200 dark:hover:bg-slate-700 cursor-pointer"
                            >
                              {isExpanded ? "Hide Breakdown" : "View Breakdown"}
                            </button>
                          </div>
                        ) : (
                          <input
                            type="number"
                            className="w-32 text-right text-sm font-medium px-3 py-1.5 border border-border rounded-lg bg-white dark:bg-slate-900 text-emerald-600 dark:text-emerald-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                            value={settleOverrides[`inv_${inv.id}`] !== undefined ? settleOverrides[`inv_${inv.id}`] : (preview?.allocated || '')}
                            onChange={e => handleOverrideChange(`inv_${inv.id}`, e.target.value, inv.balance_amount)}
                            min="0"
                            max={inv.balance_amount}
                            placeholder="0"
                          />
                        )}
                      </div>

                      {/* Expandable breakdown for mobile */}
                      {hasItems && isExpanded && (
                        <div className="pt-3 border-t border-border/60 space-y-3 bg-slate-50/50 dark:bg-slate-800/20 p-2.5 rounded-lg">
                          {preview?.items?.map(item => (
                            <div key={item.id} className="space-y-1">
                              <div className="text-xs font-semibold text-slate-700 dark:text-slate-300">{item.description}</div>
                              <div className="flex justify-between items-center gap-2">
                                <span className="text-[11px] text-red-400 font-semibold">Due: ₹{Number(item.balance_amount).toLocaleString()}</span>
                                <input
                                  type="number"
                                  className="w-32 text-right text-sm font-medium px-3 py-1.5 border border-border rounded-lg bg-white dark:bg-slate-900 text-emerald-600 dark:text-emerald-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                                  value={settleOverrides[`item_${item.id}`] !== undefined ? settleOverrides[`item_${item.id}`] : (item.allocated || '')}
                                  onChange={e => handleOverrideChange(`item_${item.id}`, e.target.value, item.balance_amount)}
                                  min="0"
                                  max={item.balance_amount}
                                  placeholder="0"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {settleInvoices.length === 0 && <div className="text-muted text-sm py-4 text-center">{t("msg_no_pending_invoices")}</div>}
              </div>
            </div>

            {/* 4. Sticky Footer */}
            <div className="sticky bottom-0 bg-white dark:bg-slate-900 border-t border-border px-4 pt-4 z-20 -mx-4">
              {settleExcess > 0 && !isOverAllocated && (
                <Alert type="info">
                  ₹{settleExcess.toLocaleString()} will be saved as Advance Balance and automatically deducted from the next month's bill.
                </Alert>
              )}
              {isOverAllocated && (
                <Alert type="error">
                  Total allocated amount exceeds received amount! Please reduce the manual allocations.
                </Alert>
              )}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 mt-4 border-t border-border pt-4 pb-4 sm:pb-0">
                <div className="flex justify-between sm:justify-start gap-4 sm:gap-8 text-sm px-2">
                  <div>
                    <span className="text-slate-400 dark:text-slate-500 block text-[10px] uppercase font-bold tracking-widest mb-1">{t("col_outstanding")}</span>
                    <span className="font-semibold text-ink dark:text-red-400 text-red-500 text-2xl leading-none">
                      ₹{Number(settleInvoices.reduce((sum, inv) => sum + parseFloat(inv.balance_amount), 0)).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-right sm:text-left">
                    <span className="text-slate-400 dark:text-slate-500 block text-[10px] uppercase font-bold tracking-widest mb-1">{t("label_allocated")}</span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-500 text-2xl leading-none">
                      ₹{Number(settlePreview.reduce((sum, inv) => sum + (inv.allocated || 0), 0)).toLocaleString()}
                    </span>
                  </div>
                </div>
                <Btn
                  variant="primary"
                  className="justify-center px-4 sm:px-8 py-3 text-sm font-bold rounded-xl shadow-lg shadow-primary/20 w-full sm:w-auto"
                  loading={settleLoading}
                  onClick={handleSettle}
                  disabled={!settleForm.received_amount || isOverAllocated}
                >{t("btn_generate_receipt")}</Btn>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {ledgerModal && (
        <Modal title={`Maintenance Ledger - ${ledgerModal.unit}`} onClose={() => setLedgerModal(null)} xxl>
          {ledgerLoading ? (
            <div className="text-center py-6 text-muted">{t("msg_loading_ledger")}</div>
          ) : (
            <div className="space-y-4">
              <div className="flex gap-4">
                <MetricCard label={t("label_current_advance")} value={`₹${Number(ledgerData?.advance_balance || 0).toLocaleString()}`} color="blue" icon="trend_up" />
                <MetricCard label={t("label_current_pending")} value={`₹${Number(ledgerData?.pending_dues || 0).toLocaleString()}`} color="red" icon="alert" />
              </div>

              <div className="flex border-b border-border">
                <button
                  onClick={() => setLedgerTab("all")}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors cursor-pointer ${ledgerTab === "all"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted hover:text-ink"
                    }`}
                >{t("label_all_transactions")}</button>
                <button
                  onClick={() => setLedgerTab("invoices")}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors cursor-pointer ${ledgerTab === "invoices"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted hover:text-ink"
                    }`}
                >{t("label_invoice_history")}</button>
              </div>

              {ledgerTab === "all" ? (
                <DataTable
                  cols={["Date", "Type", "Reference", "Debit (Billed)", "Credit (Paid)", "Balance", ""]}
                  rows={(ledgerData?.ledger || []).map(entry => [
                    entry.date,
                    <Badge color={entry.type === 'Invoice' ? 'blue' : entry.type === 'Penalty' ? 'red' : entry.type === 'Payment' ? 'green' : 'amber'} sm>{entry.type}</Badge>,
                    <span className="text-xs">{entry.reference}</span>,
                    entry.debit > 0 ? `₹${Number(entry.debit).toLocaleString()}` : '-',
                    entry.credit > 0 ? <span className="text-emerald-600 dark:text-emerald-500 font-semibold">₹{Number(entry.credit).toLocaleString()}</span> : '-',
                    <span className="font-bold">
                      {entry.balance < 0
                        ? `₹${Math.abs(Number(entry.balance)).toLocaleString()} (Cr)`
                        : `₹${Number(entry.balance).toLocaleString()}`}
                    </span>,
                    entry.type === 'Payment' ? (
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={async () => {
                          try {
                            const paymentId = entry.id.replace('pay_', '');
                            await maintenanceService.downloadReceiptPdf(paymentId, entry.reference);
                            toastSuccess('Receipt downloaded.');
                          } catch (e) {
                            toastError('Failed to download receipt.');
                          }
                        }} className="text-primary hover:text-primary/80 p-1" title={t("btn_download_receipt")}>
                          <Icon name="download" size={14} />
                        </button>
                        <button onClick={async () => {
                          if (window.confirm('Are you sure you want to reverse this payment? This action cannot be undone.')) {
                            try {
                              await maintenanceService.reversePayment(entry.id.replace('pay_', ''));
                              toastSuccess('Payment reversed successfully.');
                              openLedgerModal(ledgerModal);
                              refreshData();
                            } catch (e) {
                              toastError(e.response?.data?.message || 'Failed to reverse payment.');
                            }
                          }
                        }} className="text-red-400 hover:text-red-600 p-1" title={t("btn_reverse_payment")}>
                          <Icon name="trash" size={14} />
                        </button>
                      </div>
                    ) : entry.type === 'Invoice' ? (
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={async () => {
                          try {
                            const invoiceId = entry.id.replace('inv_', '');
                            if (invoiceId === 'opening_arrears') {
                              toastError('Opening arrears balance does not have an invoice document.');
                              return;
                            }
                            await maintenanceService.downloadInvoicePdf(invoiceId, entry.reference);
                            toastSuccess('Invoice downloaded.');
                          } catch (e) {
                            toastError(e?.message || 'Failed to download invoice.');
                          }
                        }} className="text-primary hover:text-primary/80 p-1" title={t("btn_download_invoice")}>
                          <Icon name="download" size={14} />
                        </button>
                      </div>
                    ) : null
                  ])}
                />
              ) : (
                <DataTable
                  cols={["Date", "Invoice No", "Total Amount", "Paid Amount", "Pending Balance", "Status", "Action"]}
                  rows={(ledgerData?.ledger || [])
                    .filter(entry => entry.type === 'Invoice')
                    .map(entry => {
                      const statusColor = entry.status === 'paid' ? 'green' : entry.status === 'partial' ? 'amber' : 'red';
                      return [
                        entry.date,
                        <span className="font-semibold">{entry.reference}</span>,
                        `₹${Number(entry.debit).toLocaleString()}`,
                        `₹${Number(entry.paid_amount || 0).toLocaleString()}`,
                        <span className={entry.balance_amount > 0 ? "text-red-500 dark:text-red-400 font-semibold" : "text-muted"}>
                          {entry.balance_amount > 0 ? `₹${Number(entry.balance_amount).toLocaleString()}` : 'Nil'}
                        </span>,
                        <Badge color={statusColor} sm className="capitalize">{entry.status}</Badge>,
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={async () => {
                            try {
                              const invoiceId = entry.id.replace('inv_', '');
                              if (invoiceId === 'opening_arrears') {
                                toastError('Opening arrears balance does not have an invoice document.');
                                return;
                              }
                              await maintenanceService.downloadInvoicePdf(invoiceId, entry.reference);
                              toastSuccess('Invoice downloaded.');
                            } catch (e) {
                              toastError(e?.message || 'Failed to download invoice.');
                            }
                          }} className="text-primary hover:text-primary/80 p-1" title={t("btn_download_invoice")} >
                            <Icon name="download" size={14} />
                          </button>
                        </div>
                      ];
                    })
                  }
                />
              )}
              <Pagination page={ledgerPage} total={ledgerTab === "all" ? (ledgerData?.ledger || []).length : (ledgerData?.ledger || []).filter(entry => entry.type === 'Invoice').length} perPage={10} onPage={fetchLedgerPage} />
            </div >
          )}
        </Modal >
      )}

      {
        selectedRows.size > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-border shadow-xl px-4 sm:px-6 py-3 z-50 flex flex-wrap items-center gap-3">
            <span className="text-sm font-bold text-ink">{selectedRows.size} unit{selectedRows.size > 1 ? 's' : ''} selected</span>
            <div className="flex gap-2 flex-wrap">
              {/* Generate bills button hidden per request */}
              <Btn sm icon="mail" loading={loadingReminders} onClick={async () => { try { setLoadingReminders(true); const res = await maintenanceService.sendReminders([...selectedRows], blockFilter); toastSuccess(`${res.data?.sent ?? 0} reminders sent.`); setSelectedRows(new Set()); } catch (err) { toastError(err?.message || 'Failed to send reminders.'); } finally { setLoadingReminders(false); } }}>{t("btn_remind")}</Btn>
              <Btn sm icon="download" loading={loadingExport} onClick={async () => { try { setLoadingExport(true); await maintenanceService.exportData(yearlyViewFY, 'csv'); toastSuccess('Export ready.'); setSelectedRows(new Set()); } catch { toastError('Export failed.'); } finally { setLoadingExport(false); } }}>{t("btn_export_selected")}</Btn>
              <Btn sm onClick={() => setSelectedRows(new Set())}>{t("btn_clear_selection")}</Btn>
            </div>
          </div>
        )
      }
      {/* FAB for Mobile */}
      {!isStrictMember && canCreate && !addUnitModal && !unitDetailModal && !receiptModal && !settleModal && !ledgerModal && createPortal(
        <button
          onClick={() => {
            if (unitLimit !== -1 && totalRecords >= unitLimit) {
              toastError(`Unit limit reached (${unitLimit}/${unitLimit}). Please upgrade your plan.`);
            } else {
              setAddUnitModal(true);
            }
          }}
          className="sm:hidden fixed bottom-6 right-6 w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center shadow-card-lg z-50 border-0 cursor-pointer active:scale-95 transition-transform hover:bg-primary-dark"
          style={{ padding: 0 }}
        >
          <Icon name="plus" size={24} />
        </button>,
        document.body
      )}
    </div >
  );
}
